import ComposerTextInput from './ComposerTextInput'
import { resolveDraftConflict, useDraftSync } from '../lib/composerDraftSync'
import { lazy, Suspense, useDeferredValue, useEffect, useEffectEvent, useMemo, useRef, useState, type ChangeEvent, type ClipboardEvent, type KeyboardEvent, type PointerEvent } from 'react'
import { flushSync } from 'react-dom'
import { Send, Paperclip, Mic, X, Loader2, FileText, FileAudio, Video as VideoIcon, File as FileIcon, AlertCircle, AlertTriangle, Pencil, Info, Sparkles, TextCursorInput, Cpu, Delete as DeleteIcon } from 'lucide-react'
import {
  describeRuntime,
  resolveRuntimeSelection,
  runtimeConversationKey,
  type RuntimePick,
} from '../lib/lastTurnRuntime'
import type { LastTurnRuntime } from '../lib/apiModels'
import { clsx } from 'clsx'
import { useSendMessage, useSkillDoc, useSkillIndex, useSkills, useStageOutgoingMessage, type Agent, type SendMessageInput, type Skill, type SendAttachment } from '../lib/api'
import { draftTargetKey, useUi } from '../store/ui'
import { rememberImage } from '../lib/recentImages'
import { discardPendingShare } from '../lib/pendingShares'
import { enqueueOutbox, newOutboxId, type OutboxAttachment } from '../lib/outbox'
import {
  clearPersistedPendingAttachments,
  loadPersistedPendingAttachments,
  savePersistedPendingAttachments,
  type PersistedPendingAttachment,
} from '../lib/pendingAttachments'
import AttachMenu from './AttachMenu'
import SlashMenu from './SlashMenu'
import MarkdownBody from './MarkdownBody'
import { appendDictationText, mergeDictationText, preserveDictationProgress } from '../lib/dictationText'
import { recordDictationDiagnostic, type DictationDiagnosticEvent } from '../lib/dictationDiagnostics'
import {
  deleteDictationRecovery,
  listDictationRecoveriesForDraft,
  saveDictationRecovery,
  type DictationRecoveryItem,
  type DictationRecoveryState,
} from '../lib/dictationRecovery'
import { commandNameFromDraft, findSlashTrigger, type SlashTrigger } from '../lib/slashCommands'
import {
  CONTROLLED_DELETE_INITIAL_DELAY_MS,
  accelerateComposerDelete,
  controlledDeleteIntervalMs,
  createDeleteAccelerationState,
  deleteComposerControlled,
} from '../lib/deleteAcceleration'
import { continueNumberedList } from '../lib/numberedList'
import { parseVoiceReplyCommand } from '../lib/voiceReplyCommand'

const ImageEditModal = lazy(() => import('./ImageEditModal'))

// v7 replaces Fable 5 with Fable 5.1 while preserving every other explicit
// model choice and the independently stored thinking level.
const CURSOR_MODEL_KEY = 'agentchat:cursor-model:v7'
const CURSOR_LEGACY_MODEL_KEY = 'agentchat:cursor-model:v6'
const CURSOR_EFFORT_KEY = 'agentchat:cursor-effort:v1'
// v4 moves both accounts from GPT-6 Sol and Astra to GPT-6.1 Sol.
// Effort remains independently stored.
const CODEX_MODEL_KEY = 'agentchat:codex-model:v4'
const CODEX_EFFORT_KEY = 'agentchat:codex-effort:v1'
const CURSOR_MODEL_OPTIONS = [
  { value: 'claude-opus-5-5', label: 'Opus 5.5', efforts: ['auto', 'low', 'medium', 'high', 'xhigh', 'max'] },
  { value: 'claude-fable-5-1', label: 'Fable 5.1', efforts: ['auto', 'low', 'medium', 'high', 'xhigh', 'max'] },
  { value: 'claude-sonnet-5-5', label: 'Sonnet 5.5', efforts: ['auto', 'low', 'medium', 'high', 'xhigh', 'max'] },
  { value: 'grok-4.7', label: 'Grok 4.7', efforts: ['auto', 'low', 'medium', 'high', 'xhigh'] },
  { value: 'gemini-4-argon', label: 'Gemini 4 Argon', efforts: ['auto', 'low', 'medium', 'high'] },
]
const CURSOR_EFFORT_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'xhigh', label: 'Extra High' },
  { value: 'max', label: 'Max' },
]
const CODEX_MODEL_OPTIONS = [
  { value: 'gpt-6.1-sol', label: 'GPT-6.1 Sol', efforts: ['low', 'medium', 'high', 'xhigh', 'max', 'ultra'] },
  { value: 'gpt-6-luna', label: 'GPT-6 Luna', efforts: ['low', 'medium', 'high', 'xhigh', 'max'] },
  { value: 'gpt-6-astra', label: 'GPT-6 Astra', efforts: ['low', 'medium', 'high', 'xhigh', 'max', 'ultra'] },
]
const CODEX_EFFORT_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'xhigh', label: 'Extra High' },
  { value: 'max', label: 'Max' },
  { value: 'ultra', label: 'Ultra' },
]
// v6 replaces Fable 5 with Fable 5.1. The v4 effort key deliberately stays
// unchanged so every lane keeps its thinking level.
const CLAUDE_EFFORT_KEY = 'agentchat:claude-effort:v4'
const CLAUDE_MODEL_KEY = 'agentchat:claude-model:v6'
const CLAUDE_LEGACY_MODEL_KEY = 'agentchat:claude-model:v5'
const CLAUDE_MODEL_OPTIONS = [
  { value: 'claude-opus-5-5', label: 'Opus 5.5' },
  { value: 'claude-fable-5-1', label: 'Fable 5.1' },
  { value: 'claude-sonnet-5-5', label: 'Sonnet 5.5' },
]
const CLAUDE_EFFORT_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'xhigh', label: 'Extra High' },
  { value: 'max', label: 'Max' },
  { value: 'ultracode', label: 'Ultracode' },
]
const CLAUDE_EFFORT_VALUES = CLAUDE_EFFORT_OPTIONS.map((option) => option.value)
function claudeEffortsForModel(): string[] {
  return CLAUDE_EFFORT_VALUES
}
const COMPOSER_AUTOFOCUS_RETRY_DELAYS_MS = [0, 120, 360, 800]
const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)

function mergePendingShareText(currentDraft: string, sharedText: string): string {
  const trimmedSharedText = sharedText.trim()
  if (!trimmedSharedText) return currentDraft
  const trimmedCurrentDraft = currentDraft.trimEnd()
  if (!trimmedCurrentDraft) return trimmedSharedText
  return `${trimmedCurrentDraft}\n\n${trimmedSharedText}`
}

function lanePreferenceKey(key: string, chatId: string): string {
  return `${key}:lane:${encodeURIComponent(chatId)}`
}

function readLanePreference(key: string, chatId: string): string | null {
  const scopedKey = lanePreferenceKey(key, chatId)
  const saved = window.localStorage.getItem(scopedKey)
  if (saved !== null) return saved
  // Snapshot the old shared preference once. New picks never write that key.
  const legacy = window.localStorage.getItem(key)
  if (legacy !== null) window.localStorage.setItem(scopedKey, legacy)
  return legacy
}

function readCursorModel(chatId: string): string {
  if (typeof window === 'undefined') return 'claude-opus-5-5'
  try {
    const saved = readLanePreference(CURSOR_MODEL_KEY, chatId)
    const value = saved === 'grok-4.6' ? 'grok-4.7' : saved
    if (CURSOR_MODEL_OPTIONS.some((option) => option.value === value)) {
      if (value !== saved) writeCursorModel(chatId, value!)
      return value!
    }
    const legacyValue = readLanePreference(CURSOR_LEGACY_MODEL_KEY, chatId)
    const migratedValue = legacyValue === 'grok-4.6'
      ? 'grok-4.7'
      : legacyValue === 'claude-fable-5'
      ? 'claude-fable-5-1'
      : CURSOR_MODEL_OPTIONS.some((option) => option.value === legacyValue)
        ? legacyValue!
        : 'claude-opus-5-5'
    window.localStorage.setItem(lanePreferenceKey(CURSOR_MODEL_KEY, chatId), migratedValue)
    return migratedValue
  } catch {
    return 'claude-opus-5-5'
  }
}

// Turns without a thinking override in these chats run their Cursor profile
// effort (MODEL_AGENTCHAT_*_CURSOR_EFFORT), not Auto.
const CURSOR_PROFILE_DEFAULT_EFFORT: Record<string, string> = {
  'pwa-auto-x-links': 'medium',
  'pwa-auto-youtube': 'medium',
}

function writeCursorModel(chatId: string, value: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(lanePreferenceKey(CURSOR_MODEL_KEY, chatId), value)
  } catch {
    // Local persistence is a convenience; send still works without it.
  }
}

function cursorModelSupportsThinking(value: string): boolean {
  return cursorEffortsForModel(value).some((effort) => effort !== 'auto')
}

function cursorEffortsForModel(value: string): string[] {
  return CURSOR_MODEL_OPTIONS.find((option) => option.value === value)?.efforts ?? ['auto']
}

function readCursorEffort(chatId: string): string {
  if (typeof window === 'undefined') return 'auto'
  try {
    const value = readLanePreference(CURSOR_EFFORT_KEY, chatId)
    return CURSOR_EFFORT_OPTIONS.some((option) => option.value === value) ? value! : 'auto'
  } catch {
    return 'auto'
  }
}

function writeCursorEffort(chatId: string, value: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(lanePreferenceKey(CURSOR_EFFORT_KEY, chatId), value)
  } catch {
    // Local persistence is a convenience; send still works without it.
  }
}

function readCodexModel(chatId: string): string {
  if (typeof window === 'undefined') return 'gpt-6.1-sol'
  try {
    const value = readLanePreference(CODEX_MODEL_KEY, chatId)
    if (CODEX_MODEL_OPTIONS.some((option) => option.value === value)) return value!
    // Retire the previous global model selection while preserving effort.
    window.localStorage.setItem(lanePreferenceKey(CODEX_MODEL_KEY, chatId), 'gpt-6.1-sol')
    return 'gpt-6.1-sol'
  } catch {
    return 'gpt-6.1-sol'
  }
}

// Both Codex logins run every listed model, so the account never narrows the picker.
function codexModelOptionsForAgent(_agent: Agent | undefined) {
  return CODEX_MODEL_OPTIONS
}

function writeCodexModel(chatId: string, value: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(lanePreferenceKey(CODEX_MODEL_KEY, chatId), value)
  } catch {
    // Local persistence is a convenience; send still works without it.
  }
}

function codexEffortsForModel(value: string): string[] {
  return CODEX_MODEL_OPTIONS.find((option) => option.value === value)?.efforts ?? ['high']
}

function readCodexEffort(chatId: string): string {
  if (typeof window === 'undefined') return 'high'
  try {
    const value = readLanePreference(CODEX_EFFORT_KEY, chatId)
    return CODEX_EFFORT_OPTIONS.some((option) => option.value === value) ? value! : 'high'
  } catch {
    return 'high'
  }
}

function writeCodexEffort(chatId: string, value: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(lanePreferenceKey(CODEX_EFFORT_KEY, chatId), value)
  } catch {
    // Local persistence is a convenience; send still works without it.
  }
}

function readClaudeEffort(chatId: string): string {
  if (typeof window === 'undefined') return 'high'
  try {
    const value = readLanePreference(CLAUDE_EFFORT_KEY, chatId)
    return CLAUDE_EFFORT_OPTIONS.some((option) => option.value === value) ? value! : 'high'
  } catch {
    return 'high'
  }
}

function readClaudeModel(chatId: string): string {
  if (typeof window === 'undefined') return 'claude-opus-5-5'
  try {
    const value = readLanePreference(CLAUDE_MODEL_KEY, chatId)
    if (CLAUDE_MODEL_OPTIONS.some((option) => option.value === value)) return value!
    const legacyValue = readLanePreference(CLAUDE_LEGACY_MODEL_KEY, chatId)
    const migratedValue = legacyValue === 'claude-fable-5'
      ? 'claude-fable-5-1'
      : CLAUDE_MODEL_OPTIONS.some((option) => option.value === legacyValue)
        ? legacyValue!
        : 'claude-opus-5-5'
    window.localStorage.setItem(lanePreferenceKey(CLAUDE_MODEL_KEY, chatId), migratedValue)
    return migratedValue
  } catch {
    return 'claude-opus-5-5'
  }
}

function writeClaudeModel(chatId: string, value: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(lanePreferenceKey(CLAUDE_MODEL_KEY, chatId), value)
  } catch {
    // Local persistence is a convenience; send still works without it.
  }
}

function writeClaudeEffort(chatId: string, value: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(lanePreferenceKey(CLAUDE_EFFORT_KEY, chatId), value)
  } catch {
    // Local persistence is a convenience; send still works without it.
  }
}

/** A file the user has attached but not yet sent. Tracked locally so we
 *  can show a preview chip with upload progress and let Mark cancel /
 *  retry / drop individual ones before pressing Send. */
type PendingAttachment = {
  clientId: string
  filename: string
  kind: SendAttachment['kind']
  mimeType: string | null
  sizeBytes: number | null
  /** Object URL for image preview while it uploads (revoked on send/clear). */
  previewUrl: string | null
  /** Server-assigned id once upload completes. */
  uploadedId: string | null
  localPath: string | null
  status: 'ready' | 'compressing' | 'uploading' | 'uploaded' | 'error'
  error: string | null
  /** Original picked/pasted/edited file blob — kept in memory so the
   *  Edit pencil on image chips can re-open the editor without forcing
   *  a redownload from the server. Cleared on send via setPending([]). */
  originalFile: File | null
}

const RECENT_SKILLS_KEY = 'agentchat:recent-skills:v1'
const PINNED_QUICK_SKILL_NAMES = ['to-work-email', 'to-gabs-job', 'image-compare']
const COMPOSER_ACTION_BUTTON_WIDTH_PX = 44
const COMPOSER_ACTION_BUTTON_GAP_PX = 8
const COMPOSER_ACTION_STACKED_WIDTH_PX = COMPOSER_ACTION_BUTTON_WIDTH_PX
const COMPOSER_ACTION_HORIZONTAL_WIDTH_PX =
  COMPOSER_ACTION_BUTTON_WIDTH_PX * 2 + COMPOSER_ACTION_BUTTON_GAP_PX
const COMPOSER_LEADING_BUTTON_WIDTH_PX = 28
const COMPOSER_LEADING_STACKED_WIDTH_PX = COMPOSER_LEADING_BUTTON_WIDTH_PX
const COMPOSER_LEADING_HORIZONTAL_WIDTH_PX = COMPOSER_LEADING_BUTTON_WIDTH_PX * 2
const COMPOSER_COLLAPSE_HYSTERESIS_PX = 32

function readRecentSkillNames(): string[] {
  if (typeof window === 'undefined') return []
  try {
    const parsed = JSON.parse(window.localStorage.getItem(RECENT_SKILLS_KEY) || '[]')
    if (!Array.isArray(parsed)) return []
    return parsed.filter((name): name is string => typeof name === 'string' && !!name).slice(0, 12)
  } catch {
    return []
  }
}

function writeRecentSkillNames(names: string[]): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(RECENT_SKILLS_KEY, JSON.stringify(names.slice(0, 12)))
  } catch {
    // Local recency is a convenience layer; the backend usage index still works.
  }
}

function textareaWrapsPastOneLineAtWidth(
  source: HTMLTextAreaElement,
  widthPx: number,
  singleLineHeightPx: number,
): boolean {
  if (typeof document === 'undefined') return false
  if (widthPx <= 0) return true
  const style = window.getComputedStyle(source)
  const probe = document.createElement('textarea')
  probe.value = source.value || ' '
  probe.rows = 1
  probe.setAttribute('aria-hidden', 'true')
  Object.assign(probe.style, {
    position: 'absolute',
    top: '-10000px',
    left: '-10000px',
    visibility: 'hidden',
    overflow: 'hidden',
    height: '0px',
    minHeight: '0px',
    maxHeight: 'none',
    width: `${widthPx}px`,
    boxSizing: 'border-box',
    border: style.border,
    padding: style.padding,
    font: style.font,
    letterSpacing: style.letterSpacing,
    textTransform: style.textTransform,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
  })
  document.body.appendChild(probe)
  const wraps = probe.scrollHeight > singleLineHeightPx + 4
  probe.remove()
  return wraps
}

function inputTypeFromNativeEvent(event: Event): string | null {
  const inputType = (event as Event & { inputType?: unknown }).inputType
  return typeof inputType === 'string' ? inputType : null
}

function isComposingNativeInput(event: Event): boolean {
  return (event as Event & { isComposing?: boolean }).isComposing === true
}

type DictationState = 'idle' | 'starting' | 'listening' | 'stopping' | 'error'
const DICTATION_PREVIEW_INTERVAL_MS = 5_000
const DICTATION_TRANSCRIBE_SLOW_NOTICE_MS = 45_000
const DICTATION_TRANSCRIBE_HARD_TIMEOUT_MS = 150_000
const DICTATION_MIN_FINAL_AUDIO_MS = 700
type DictationEngine = 'openai-transcribe'

export type AutoVoiceReplyRequest = {
  id: string
  replyToMessageId: string | null
}

function dictationStartErrorMessage(error: unknown): string {
  const name = error instanceof Error ? error.name : ''
  const message = error instanceof Error ? error.message : String(error || '')
  if (
    name === 'NotAllowedError' ||
    name === 'SecurityError' ||
    /permission denied|not allowed|permission dismissed/i.test(message)
  ) {
    return 'microphone permission blocked; enable Microphone for AgentChat'
  }
  if (name === 'NotFoundError' || /requested device not found|no microphone/i.test(message)) {
    return 'no microphone found for dictation'
  }
  return message || 'dictation failed'
}

function isDictationPermissionError(error: unknown): boolean {
  const name = error instanceof Error ? error.name : ''
  const message = error instanceof Error ? error.message : String(error || '')
  return (
    name === 'NotAllowedError' ||
    name === 'SecurityError' ||
    /permission denied|not allowed|permission dismissed/i.test(message)
  )
}

const EAGER_UPLOAD_MAX_BYTES = 16 * 1024 * 1024

function _newClientId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function _kindForFile(file: File): SendAttachment['kind'] {
  if (file.type.startsWith('image/')) return 'image'
  if (file.type.startsWith('video/')) return 'video'
  if (file.type.startsWith('audio/')) return 'audio'
  if (file.type === 'application/pdf') return 'pdf'
  return 'document'
}

function revivePendingAttachment(att: PersistedPendingAttachment): PendingAttachment {
  return {
    clientId: att.clientId,
    filename: att.filename,
    kind: att.kind,
    mimeType: att.mimeType,
    sizeBytes: att.sizeBytes,
    previewUrl: null,
    uploadedId: att.uploadedId,
    localPath: att.localPath,
    status: 'uploaded',
    error: null,
    originalFile: null,
  }
}

function discardUploadedPendingAttachment(att: PendingAttachment): void {
  if (!att.uploadedId) return
  void fetch(`/api/uploads/${encodeURIComponent(att.uploadedId)}/discard`, {
    method: 'POST',
    credentials: 'include',
  }).catch((err) => {
    console.warn('discard pending upload failed:', err)
  })
}

function pendingToOutboxAttachment(att: PendingAttachment): OutboxAttachment | null {
  if (att.uploadedId && att.localPath) {
    return {
      id: att.uploadedId,
      kind: att.kind,
      filename: att.filename,
      mime_type: att.mimeType,
      size_bytes: att.sizeBytes,
      local_path: att.localPath,
      preview_url: att.previewUrl || undefined,
    }
  }
  if (!att.originalFile) return null
  return {
    id: att.clientId,
    kind: att.kind,
    filename: att.filename,
    mime_type: att.mimeType,
    size_bytes: att.sizeBytes,
    local_path: null,
    preview_url: att.previewUrl || undefined,
    file: att.originalFile,
  }
}

/**
 * Client-side image compression. Phone cameras produce 4-12 MB JPEGs that
 * uploads-on-mobile-data CRAWL through. We resize to max 1920px on the
 * long edge and re-encode at 0.85 JPEG quality — typically 80-90% smaller
 * with no perceptible loss for chat usage.
 *
 * Falls through unmodified for non-images (PDFs, video, etc) and for
 * images already < 500KB (compression overhead would not pay off).
 */
async function _compressImageIfBeneficial(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file
  if (file.size < 500_000) return file
  if (file.type === 'image/gif') return file  // animated; don't flatten
  try {
    const bitmap = await createImageBitmap(file)
    const MAX_EDGE = 1920
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height))
    if (scale === 1) {
      // Already small enough; just re-encode at lower quality if huge.
      if (file.size < 2_000_000) return file
    }
    const w = Math.round(bitmap.width * scale)
    const h = Math.round(bitmap.height * scale)
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return file
    ctx.drawImage(bitmap, 0, 0, w, h)
    const blob: Blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('canvas toBlob returned null'))),
        'image/jpeg',
        0.85,
      )
    })
    // Compression flopped (rare — small icons / re-encode of already
    // optimised JPEGs). Send the original.
    if (blob.size >= file.size) return file
    const newName = file.name.replace(/\.(png|webp|heic|heif|jpe?g)$/i, '.jpg')
    return new File([blob], newName, { type: 'image/jpeg', lastModified: file.lastModified })
  } catch (err) {
    console.warn('image compression failed; sending original', err)
    return file
  }
}

/**
 * Composer.
 *
 * Behaviour notes:
 *   - **Phone Enter = newline; desktop Enter = send.** Ctrl/Cmd+Enter sends
 *     on either device. IME confirmation never sends.
 *   - **Mic button starts recording on first click** (no second-tap dialog).
 *     Browser permission prompt happens once per origin.
 *   - **Optimistic submit** — the outgoing bubble is inserted synchronously
 *     on tap, before durable outbox promotion and server delivery continue.
 *   - **Slash menu auto-opens** on `/` at the cursor, including mid-draft;
 *     auto-closes after pick or Esc; re-opens on the next `/`.
 */
export default function Composer({
  chatId,
  topicId = null,
  autoFocus = null,
  autoVoiceReplyRequest = null,
  chatAgent,
  enableClaudeEffort = false,
  chatType,
  lastTurnRuntime,
  lastUsedRuntime,
}: {
  chatId: string
  topicId?: string | null
  autoVoiceReplyRequest?: AutoVoiceReplyRequest | null
  chatAgent?: Agent
  enableClaudeEffort?: boolean
  /** Direct lanes restart their provider session (full replay) on a
   *  model/thinking change; topics only change the next turn. */
  chatType?: 'user-driven' | 'auto-spawn'
  /** What this exact conversation (lane root or topic) last ran. `null` =
   *  no turn yet; `undefined` = unknown (row cached before the field). */
  lastTurnRuntime?: LastTurnRuntime | null
  /** What the lane root last ran, including before its latest reset. */
  lastUsedRuntime?: LastTurnRuntime | null
  /** Focus policy resolved by ChatPage on first messages-load:
   *   - `true`  → focus the textarea for a fresh reset/empty chat
   *   - `false` → leave focus alone (existing chats and topics read first)
   *   - `null`  → not yet decided; do nothing this render
   *
   *  We MUST NOT use the HTML `autoFocus` attribute — that fires once
   *  on mount with whatever the prop is at mount time, which is too
   *  early (messages haven't loaded → unread count is 0 → keyboard
   *  pops up under Mark's reading thumb). Using an effect lets the
   *  decision actually flow through after the unread count is known. */
  autoFocus?: boolean | null
}) {
  const send = useSendMessage(chatId)
  const stageOutgoingMessage = useStageOutgoingMessage(chatId)
  const draftKey = draftTargetKey(chatId, topicId)
  const draftConflict = useDraftSync((s) => s.conflicts[draftKey])
  const draftSyncOffline = useDraftSync((s) => s.offline)
  const storedDraft = useUi((s) => s.draftsByChat[draftKey] || '')
  const persistDraft = useUi((s) => s.setDraft)
  const clearStoredDraft = useUi((s) => s.clearDraft)
  const [draft, setDraftState] = useState(storedDraft)
  const [sendError, setSendError] = useState<string | null>(null)
  const draftRef = useRef(storedDraft)
  const previousDraftLengthRef = useRef(storedDraft.length)
  const draftPersistTimerRef = useRef<number | null>(null)
  const pendingDraftPersistRef = useRef<string | null>(null)
  const lastPersistedDraftRef = useRef(storedDraft)
  // composeMode (queue vs force-immediate) was a global toggle that was
  // removed in favour of per-message Force-now buttons on queued bubbles.
  // The store fields still exist so settings/migration is forward-compat.
  const textSizeScale = useUi((s) => s.textSizeScale)

  const taRef = useRef<HTMLTextAreaElement>(null)
  const [busy, setBusy] = useState(false)
  const [draftLocked, setDraftLocked] = useState(false)
  const [pendingStateKey, setPendingStateKey] = useState(draftKey)
  const [pending, setPending] = useState<PendingAttachment[]>(() =>
    loadPersistedPendingAttachments(draftKey).map(revivePendingAttachment),
  )
  const [voiceUploading, setVoiceUploading] = useState(false)
  const [attachMenuOpen, setAttachMenuOpen] = useState(false)
  // Reserved for explicit pre-stage image edits. The normal pick/paste path
  // stages immediately, then the chip pencil re-opens the editor on demand.
  const [editQueue, setEditQueue] = useState<File[]>([])
  // Set ONLY by the on-demand pencil button on a pending chip — lets
  // the user re-edit an image they already staged without picking it
  // again.
  const [editingChipId, setEditingChipId] = useState<string | null>(null)
  const objectUrlsRef = useRef<Set<string>>(new Set())
  // Mirror of `pending` accessible inside async closures (where the React
  // state snapshot would be stale). submit() reads this after awaiting
  // in-flight uploads to decide which attachments are ready to ship.
  const pendingRef = useRef<PendingAttachment[]>([])
  useEffect(() => { pendingRef.current = pending }, [pending])
  useEffect(() => {
    if (pendingStateKey !== draftKey) return
    savePersistedPendingAttachments(draftKey, pending)
  }, [draftKey, pending, pendingStateKey])
  useEffect(() => {
    setPending((prev) => {
      for (const att of prev) {
        if (att.previewUrl) {
          URL.revokeObjectURL(att.previewUrl)
          objectUrlsRef.current.delete(att.previewUrl)
        }
      }
      return loadPersistedPendingAttachments(draftKey).map(revivePendingAttachment)
    })
    setPendingStateKey(draftKey)
  }, [draftKey])
  // Promises for every upload currently in flight (compress + POST).
  // submit() awaits these so a tap on Send during upload is captured
  // immediately and dispatches as soon as the bytes finish landing.
  const uploadPromisesRef = useRef<Set<Promise<void>>>(new Set())
  const uploadAbortRef = useRef<Map<string, AbortController>>(new Map())
  const uploadStartTimersRef = useRef<Map<string, number>>(new Map())
  const cancelledUploadIdsRef = useRef<Set<string>>(new Set())
  const attachmentsUploading = pending.some(
    (a) => a.status === 'compressing' || a.status === 'uploading',
  )
  const uploading = attachmentsUploading || voiceUploading
  const [slashDismissedKey, setSlashDismissedKey] = useState<string | null>(null)
  const [caretPosition, setCaretPosition] = useState(0)
  const [docCommandName, setDocCommandName] = useState<string | null>(null)
  const [quickSkillsOpen, setQuickSkillsOpen] = useState(false)
  const [composerMultiline, setComposerMultiline] = useState(false)
  const [composerActionsStacked, setComposerActionsStacked] = useState(false)
  const [composerDeleteVisible, setComposerDeleteVisible] = useState(false)
  const [recentSkillNames, setRecentSkillNames] = useState<string[]>(readRecentSkillNames)
  const showCursorModel = chatAgent === 'cursor'
  const showCodexControls = chatAgent === 'codex' || chatAgent === 'codex-secondary'
  const showClaudeControls = enableClaudeEffort && chatAgent === 'claude'
  // Lane defaults are loaded synchronously on navigation, avoiding a render
  // (or quick send) with the previous lane's settings. Revision refreshes picks.
  const [preferenceRevision, setPreferenceRevision] = useState(0)
  const {
    laneCursorModel, laneCursorEffort, laneCodexModel, laneCodexEffort,
    laneClaudeModel, laneClaudeEffort,
  } = useMemo(() => ({
    laneCursorModel: readCursorModel(chatId),
    laneCursorEffort: readCursorEffort(chatId),
    laneCodexModel: readCodexModel(chatId),
    laneCodexEffort: readCodexEffort(chatId),
    laneClaudeModel: readClaudeModel(chatId),
    laneClaudeEffort: readClaudeEffort(chatId),
    // Storage is external to React; explicit changes invalidate this snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [chatId, preferenceRevision])
  // Explicit picks made in the open conversation and not sent yet. Keyed by
  // conversation so switching chats or topics never carries a pick across.
  const runtimeKey = runtimeConversationKey(chatId, topicId)
  const [runtimePicks, setRuntimePicks] = useState<Record<string, RuntimePick>>({})
  const conversationPick: RuntimePick | null = runtimePicks[runtimeKey] ?? null
  function updateRuntimePick(patch: RuntimePick) {
    setRuntimePicks((current) => ({
      ...current,
      [runtimeKey]: { ...current[runtimeKey], ...patch },
    }))
  }

  const availableCodexModelOptions = useMemo(
    () => codexModelOptionsForAgent(chatAgent),
    [chatAgent],
  )
  const cursorSelection = useMemo(() => resolveRuntimeSelection({
    lastTurn: lastTurnRuntime,
    pick: conversationPick,
    lastUsed: lastUsedRuntime,
    fallback: { model: laneCursorModel, effort: laneCursorEffort },
    // Cursor sends no effort override for Auto, so a null stored effort is Auto.
    defaults: { model: 'claude-opus-5-5', effort: CURSOR_PROFILE_DEFAULT_EFFORT[chatId] ?? 'auto' },
    modelOptions: CURSOR_MODEL_OPTIONS,
    effortsForModel: cursorEffortsForModel,
  }), [lastTurnRuntime, conversationPick, lastUsedRuntime, laneCursorModel, laneCursorEffort, chatId])
  const codexSelection = useMemo(() => resolveRuntimeSelection({
    lastTurn: lastTurnRuntime,
    pick: conversationPick,
    lastUsed: lastUsedRuntime,
    fallback: { model: laneCodexModel, effort: laneCodexEffort },
    defaults: { model: availableCodexModelOptions[0]?.value ?? 'gpt-6.1-sol', effort: 'high' },
    modelOptions: availableCodexModelOptions,
    effortsForModel: codexEffortsForModel,
  }), [lastTurnRuntime, conversationPick, lastUsedRuntime, laneCodexModel, laneCodexEffort, availableCodexModelOptions])
  const claudeSelection = useMemo(() => resolveRuntimeSelection({
    lastTurn: lastTurnRuntime,
    pick: conversationPick,
    lastUsed: lastUsedRuntime,
    fallback: { model: laneClaudeModel, effort: laneClaudeEffort },
    defaults: { model: 'claude-opus-5-5', effort: 'high' },
    modelOptions: CLAUDE_MODEL_OPTIONS,
    effortsForModel: claudeEffortsForModel,
  }), [lastTurnRuntime, conversationPick, lastUsedRuntime, laneClaudeModel, laneClaudeEffort])

  const cursorModel = cursorSelection.model
  const selectedCursorEffort = cursorSelection.effort
  const availableCursorEffortOptions = useMemo(() => {
    const allowedEfforts = cursorEffortsForModel(cursorModel)
    return CURSOR_EFFORT_OPTIONS.filter((option) => allowedEfforts.includes(option.value))
  }, [cursorModel])
  const cursorThinkingAvailable = showCursorModel && cursorModelSupportsThinking(cursorModel)
  const selectedCodexModel = codexSelection.model
  const selectedCodexEffort = codexSelection.effort
  const availableCodexEffortOptions = useMemo(() => {
    const allowedEfforts = codexEffortsForModel(selectedCodexModel)
    return CODEX_EFFORT_OPTIONS.filter((option) => allowedEfforts.includes(option.value))
  }, [selectedCodexModel])
  const claudeModel = claudeSelection.model
  const claudeEffort = claudeSelection.effort

  const activeRuntimeSelection = showClaudeControls
    ? claudeSelection
    : showCodexControls
      ? codexSelection
      : showCursorModel
        ? cursorSelection
        : null
  const activeModelOptions = showClaudeControls
    ? CLAUDE_MODEL_OPTIONS
    : showCodexControls
      ? CODEX_MODEL_OPTIONS
      : CURSOR_MODEL_OPTIONS
  const activeEffortOptions = showClaudeControls
    ? CLAUDE_EFFORT_OPTIONS
    : showCodexControls
      ? CODEX_EFFORT_OPTIONS
      : CURSOR_EFFORT_OPTIONS
  // Shown whenever the pickers differ from what this conversation last ran.
  // Direct lanes normally replay history after a runtime change. Commissioned
  // CAD controllers retain their existing session and native-work ownership.
  const runtimeDivergence = activeRuntimeSelection?.diverges && activeRuntimeSelection.lastTurn
    ? {
        lastTurnLabel: describeRuntime(activeRuntimeSelection.lastTurn, activeModelOptions, activeEffortOptions),
        selectedLabel: describeRuntime(
          { model: activeRuntimeSelection.model, effort: activeRuntimeSelection.effort },
          activeModelOptions,
          activeEffortOptions,
        ),
        restartsSession: chatType === 'user-driven' && !topicId,
        canRevert: activeRuntimeSelection.canRevert,
      }
    : null
  function revertToLastTurn() {
    const lastTurn = activeRuntimeSelection?.lastTurn
    if (!lastTurn) return
    updateRuntimePick({ model: lastTurn.model, effort: lastTurn.effort })
  }

  function setCursorModel(value: string) {
    writeCursorModel(chatId, value)
    let effort = selectedCursorEffort
    if (!cursorEffortsForModel(value).includes(effort)) {
      effort = 'auto'
      writeCursorEffort(chatId, effort)
    }
    setPreferenceRevision((revision) => revision + 1)
    updateRuntimePick({ model: value, effort })
  }

  function setCursorEffort(value: string) {
    writeCursorEffort(chatId, value)
    setPreferenceRevision((revision) => revision + 1)
    updateRuntimePick({ effort: value })
  }

  function setCodexModel(value: string) {
    writeCodexModel(chatId, value)
    let effort = selectedCodexEffort
    if (!codexEffortsForModel(value).includes(effort)) {
      effort = 'high'
      writeCodexEffort(chatId, effort)
    }
    setPreferenceRevision((revision) => revision + 1)
    updateRuntimePick({ model: value, effort })
  }

  function setCodexEffort(value: string) {
    writeCodexEffort(chatId, value)
    setPreferenceRevision((revision) => revision + 1)
    updateRuntimePick({ effort: value })
  }

  function setClaudeModel(value: string) {
    writeClaudeModel(chatId, value)
    setPreferenceRevision((revision) => revision + 1)
    updateRuntimePick({ model: value })
  }

  function setClaudeEffort(value: string) {
    writeClaudeEffort(chatId, value)
    setPreferenceRevision((revision) => revision + 1)
    updateRuntimePick({ effort: value })
  }

  // ── Recording state — embedded directly in composer (no modal) ─────────
  const [recording, setRecording] = useState(false)
  const [recSeconds, setRecSeconds] = useState(0)
  const [micError, setMicError] = useState<string | null>(null)
  const [voiceError, setVoiceError] = useState<string | null>(null)
  const recStreamRef = useRef<MediaStream | null>(null)
  const recChunksRef = useRef<Blob[]>([])
  const recRecorderRef = useRef<MediaRecorder | null>(null)
  const recTickRef = useRef<number | null>(null)

  function clearRecorderErrors() {
    setMicError(null)
    setVoiceError(null)
  }
  const [dictationState, setDictationState] = useState<DictationState>('idle')
  const [dictationError, setDictationError] = useState<string | null>(null)
  const [dictationNotice, setDictationNotice] = useState<string | null>(null)
  const [dictationRecoveries, setDictationRecoveries] = useState<DictationRecoveryItem[]>([])
  const [dictationRecoveryBusyId, setDictationRecoveryBusyId] = useState<string | null>(null)
  const [dictationPermissionSheetOpen, setDictationPermissionSheetOpen] = useState(false)
  const [autoVoiceReplyActive, setAutoVoiceReplyActiveState] = useState(false)
  const dictationStateRef = useRef<DictationState>('idle')
  const dictationEngineRef = useRef<DictationEngine | null>(null)
  const dictationBaseDraftRef = useRef('')
  const dictationFinalTextRef = useRef('')
  const dictationInterimTextRef = useRef('')
  const dictationLastPartialTextRef = useRef('')
  const dictationProgressTextRef = useRef('')
  const dictationRenderedDraftRef = useRef('')
  const dictationStopTimerRef = useRef<number | null>(null)
  const dictationHardStopTimerRef = useRef<number | null>(null)
  const dictationMediaStreamRef = useRef<MediaStream | null>(null)
  const dictationMediaRecorderRef = useRef<MediaRecorder | null>(null)
  const dictationRecordingChunksRef = useRef<Blob[]>([])
  const dictationRecorderMimeTypeRef = useRef('audio/webm')
  const dictationTranscribeAbortRef = useRef<AbortController | null>(null)
  const dictationPreviewAbortRef = useRef<AbortController | null>(null)
  const dictationPreviewTimerRef = useRef<number | null>(null)
  const dictationPreviewInFlightRef = useRef(false)
  const dictationPreviewPendingRef = useRef(false)
  const dictationPreviewSequenceRef = useRef(0)
  const dictationLastAppliedPreviewSequenceRef = useRef(0)
  const dictationFinalizingRef = useRef(false)
  const dictationRecordingStartedAtRef = useRef(0)
  const dictationRecoveryIdRef = useRef<string | null>(null)
  const dictationRecoveryCreatedAtRef = useRef(0)
  const dictationRecoverySaveTimerRef = useRef<number | null>(null)
  const dictationFallbackAttachmentIdRef = useRef<string | null>(null)
  const autoVoiceReplyActiveRef = useRef(false)
  const autoVoiceReplyPendingSendRef = useRef(false)
  const autoVoiceReplyRequestIdRef = useRef<string | null>(null)
  const autoVoiceReplyStopScheduledRef = useRef(false)
  const deleteAccelerationRef = useRef(createDeleteAccelerationState())
  const controlledDeleteTimerRef = useRef<number | null>(null)
  const controlledDeleteStartedAtRef = useRef(0)
  const controlledDeletePointerIdRef = useRef<number | null>(null)

  useEffect(() => () => {
    if (controlledDeleteTimerRef.current !== null) {
      window.clearTimeout(controlledDeleteTimerRef.current)
      controlledDeleteTimerRef.current = null
    }
  }, [])

  function flushPendingDraftPersist() {
    const pendingDraft = pendingDraftPersistRef.current
    if (draftPersistTimerRef.current !== null) {
      window.clearTimeout(draftPersistTimerRef.current)
      draftPersistTimerRef.current = null
    }
    if (pendingDraft === null) return
    pendingDraftPersistRef.current = null
    lastPersistedDraftRef.current = pendingDraft
    persistDraft(draftKey, pendingDraft)
  }

  function setDraftFast(nextDraft: string, options: { immediate?: boolean } = {}) {
    draftRef.current = nextDraft
    setDraftState(nextDraft)
    if (draftPersistTimerRef.current !== null) {
      window.clearTimeout(draftPersistTimerRef.current)
      draftPersistTimerRef.current = null
    }
    pendingDraftPersistRef.current = nextDraft
    if (options.immediate) {
      flushPendingDraftPersist()
      return
    }
    draftPersistTimerRef.current = window.setTimeout(flushPendingDraftPersist, 180)
  }

  function clearDraftFast() {
    if (draftPersistTimerRef.current !== null) {
      window.clearTimeout(draftPersistTimerRef.current)
      draftPersistTimerRef.current = null
    }
    pendingDraftPersistRef.current = null
    lastPersistedDraftRef.current = ''
    draftRef.current = ''
    setDraftState('')
    clearStoredDraft(draftKey)
  }

  function refreshDictationRecoveries() {
    void listDictationRecoveriesForDraft(draftKey)
      .then(setDictationRecoveries)
      .catch(() => setDictationRecoveries([]))
  }

  useEffect(() => {
    refreshDictationRecoveries()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey])

  useEffect(() => () => {
    flushPendingDraftPersist()
  }, [draftKey]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (storedDraft === lastPersistedDraftRef.current) return
    if (pendingDraftPersistRef.current !== null) return
    lastPersistedDraftRef.current = storedDraft
    draftRef.current = storedDraft
    setDraftState(storedDraft)
  }, [storedDraft])

  // Auto-grow textarea up to ~6 rows.
  useEffect(() => {
    const el = taRef.current
    if (!el) return
    el.style.height = '0px'
    const measuredHeight = Math.min(el.scrollHeight, 220)
    const style = window.getComputedStyle(el)
    const lineHeight = Number.parseFloat(style.lineHeight) || 20
    const paddingY = (Number.parseFloat(style.paddingTop) || 0) +
      (Number.parseFloat(style.paddingBottom) || 0)
    const singleLineHeight = lineHeight + paddingY
    const wrapsPastOneLine = measuredHeight > singleLineHeight + 4
    const textDeleted = draft.length < previousDraftLengthRef.current
    previousDraftLengthRef.current = draft.length
    const horizontalComposerExtraWidth =
      (composerMultiline
        ? COMPOSER_LEADING_HORIZONTAL_WIDTH_PX - COMPOSER_LEADING_STACKED_WIDTH_PX
        : 0) +
      (composerActionsStacked
        ? COMPOSER_ACTION_HORIZONTAL_WIDTH_PX - COMPOSER_ACTION_STACKED_WIDTH_PX
        : 0)
    // When controls are stacked, the textarea is wider. Only collapse back
    // once the draft would still fit after those controls return beside it.
    const wouldWrapIfComposerWereHorizontal =
      horizontalComposerExtraWidth > 0 &&
      !wrapsPastOneLine &&
      draft.length > 0 &&
      textareaWrapsPastOneLineAtWidth(
        el,
        el.getBoundingClientRect().width - horizontalComposerExtraWidth - COMPOSER_COLLAPSE_HYSTERESIS_PX,
        singleLineHeight,
      )
    const nextComposerMultiline = wrapsPastOneLine ||
      (composerMultiline && draft.length > 0 && (!textDeleted || wouldWrapIfComposerWereHorizontal))
    const twoLineHeight = lineHeight * 2 + paddingY
    const fourLineHeight = lineHeight * 4 + paddingY
    const nextHeight = Math.min(
      Math.max(measuredHeight, nextComposerMultiline ? twoLineHeight : measuredHeight),
      220,
    )
    const nextDeleteVisible = draft.length > 0 && measuredHeight >= fourLineHeight - 4
    el.style.height = `${nextHeight}px`
    if (composerMultiline !== nextComposerMultiline) {
      setComposerMultiline(nextComposerMultiline)
    }
    if (composerActionsStacked !== nextComposerMultiline) {
      setComposerActionsStacked(nextComposerMultiline)
    }
    if (composerDeleteVisible !== nextDeleteVisible) {
      setComposerDeleteVisible(nextDeleteVisible)
    }
  }, [composerActionsStacked, composerDeleteVisible, composerMultiline, draft, textSizeScale])

  // Smart-keyboard focus.
  //
  // ChatPage decides on first messages-load whether the composer should
  // grab focus (fresh reset/empty chat) or stay unfocused (existing chats
  // and all topics). Once decided, the prop transitions from `null` → `true|false`,
  // and we run focus() imperatively here. Doing it via the HTML autoFocus
  // attribute would have been wrong: that attribute is sampled ONCE on
  // mount, when messages haven't loaded yet → unread count looks like 0
  // → keyboard pops up under Mark's reading thumb.
  //
  // We only key the effect on `autoFocus === true`. Going to false is a
  // no-op (we don't blur — Mark might've manually tapped the textarea
  // already, and yanking focus from him would be obnoxious).
  useEffect(() => {
    if (autoFocus !== true) return
    const el = taRef.current
    if (!el) return
    let cancelled = false
    const timers = COMPOSER_AUTOFOCUS_RETRY_DELAYS_MS.map((delayMs) =>
      window.setTimeout(() => {
        if (cancelled) return
        requestAnimationFrame(() => {
          if (cancelled) return
          el.focus()
        })
      }, delayMs),
    )
    return () => {
      cancelled = true
      timers.forEach((timer) => window.clearTimeout(timer))
    }
  }, [autoFocus])

  useEffect(() => {
    function onFocusRequest(event: Event) {
      const detail = (event as CustomEvent<{ chatId?: string; draftKey?: string }>).detail
      if (detail?.draftKey && detail.draftKey !== draftKey) return
      if (!detail?.draftKey && detail?.chatId && detail.chatId !== chatId) return
      requestAnimationFrame(() => taRef.current?.focus())
    }
    window.addEventListener('agentchat:focus-composer', onFocusRequest)
    return () => window.removeEventListener('agentchat:focus-composer', onFocusRequest)
  }, [chatId, draftKey])

  // Desktop dictation destination. Keep the mobile read-first keyboard policy.
  const onDesktopPaste = useEffectEvent((event: globalThis.ClipboardEvent) => {
    const el = taRef.current
    if (!el || !event.clipboardData) return
    const text = event.clipboardData.getData('text/plain')
    const start = el.selectionStart
    const end = el.selectionEnd
    onPaste(event)
    if (text) {
      event.preventDefault()
      setTextareaValueAndCaret(
        el.value.slice(0, start) + text + el.value.slice(end),
        start + text.length,
      )
    }
    el.focus({ preventScroll: true })
  })

  useEffect(() => {
    if (isMobileDevice || draftLocked) return
    const textarea = taRef.current
    if (!textarea) return
    const editable = 'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"]'
    let frame: number | null = null
    let pointerDown = false
    let activationTimer: number | null = null
    let activationAttempts = 0
    function cancelActivationRetry() {
      if (activationTimer !== null) window.clearTimeout(activationTimer)
      activationTimer = null
    }
    function retryActivationFocus() {
      activationTimer = null
      focusComposer()
      // Native activation can reach the renderer later than the first frame.
      if (document.visibilityState === 'visible' && !document.hasFocus() && activationAttempts++ < 20) {
        activationTimer = window.setTimeout(retryActivationFocus, 50)
      }
    }
    function canReceive() {
      const active = document.activeElement
      return textarea!.isConnected && !textarea!.disabled && document.visibilityState === 'visible'
        && document.hasFocus()
        && !document.querySelector('[role="dialog"], [aria-modal="true"], dialog[open]')
        && !(active instanceof Element && active !== textarea && active.closest(editable))
    }
    function focusComposer() {
      // Do not collapse a message selection or interrupt a pointer gesture.
      if (pointerDown || document.getSelection()?.isCollapsed === false) return
      if (canReceive()) textarea!.focus({ preventScroll: true })
    }
    function scheduleFocus() {
      if (frame !== null) cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        frame = null
        focusComposer()
      })
    }
    function onAppFocus() {
      pointerDown = false
      cancelActivationRetry()
      activationAttempts = 0
      retryActivationFocus()
      // Native app activation can precede document.hasFocus() becoming true.
      scheduleFocus()
    }
    function onAppBlur() {
      pointerDown = false
      cancelActivationRetry()
    }
    function onPointerDown() {
      pointerDown = true
      cancelActivationRetry()
    }
    function onPointerEnd() {
      pointerDown = false
      // Let click handlers open their dialog/editor before choosing a target.
      scheduleFocus()
    }
    function onFocusOut(event: FocusEvent) {
      // Preserve Tab navigation to another control. Pointer clicks are handled
      // after pointerup; removed/blurred controls leave no insertion target.
      if (!event.relatedTarget || event.relatedTarget === document.body) scheduleFocus()
    }
    function onShortcut(event: globalThis.KeyboardEvent) {
      // Superwhisper's modifier-only Command shortcut may never emit a paste
      // event if its native insertion-target check finds no focused editor.
      if (event.key === 'Meta') focusComposer()
      else cancelActivationRetry()
    }
    function pasteIntoComposer(event: globalThis.ClipboardEvent) {
      if (event.defaultPrevented || !canReceive()) return
      if (event.target instanceof Element && event.target.closest(editable)) return
      onDesktopPaste(event)
    }
    scheduleFocus()
    window.addEventListener('focus', onAppFocus)
    window.addEventListener('blur', onAppBlur)
    document.addEventListener('visibilitychange', onAppFocus)
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('pointerup', onPointerEnd, true)
    document.addEventListener('pointercancel', onPointerEnd, true)
    document.addEventListener('focusout', onFocusOut)
    document.addEventListener('keydown', onShortcut, true)
    document.addEventListener('paste', pasteIntoComposer)
    return () => {
      if (frame !== null) cancelAnimationFrame(frame)
      cancelActivationRetry()
      window.removeEventListener('focus', onAppFocus)
      window.removeEventListener('blur', onAppBlur)
      document.removeEventListener('visibilitychange', onAppFocus)
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('pointerup', onPointerEnd, true)
      document.removeEventListener('pointercancel', onPointerEnd, true)
      document.removeEventListener('focusout', onFocusOut)
      document.removeEventListener('keydown', onShortcut, true)
      document.removeEventListener('paste', pasteIntoComposer)
    }
  }, [draftKey, draftLocked])

  // Web Share Target consumption. When Mark tapped a chat from the
  // chat-list "tap to attach" banner, useUi.pendingShare holds the
  // files he shared from Android's gallery. Stage them through the
  // normal fast path and pre-fill the draft if the share carried text.
  // Clear both the store entry and the IDB row so a refresh doesn't
  // re-attach.
  const pendingShare = useUi((s) => s.pendingShare)
  const setPendingShare = useUi((s) => s.setPendingShare)
  useEffect(() => {
    if (!pendingShare) return
    if (pendingShare.text) {
      const nextDraft = mergePendingShareText(draftRef.current, pendingShare.text)
      if (nextDraft !== draftRef.current) setDraftFast(nextDraft, { immediate: true })
    }
    if (pendingShare.files.length > 0) _processFiles(pendingShare.files)
    void discardPendingShare(pendingShare.id)
    setPendingShare(null)
    // Run on every fresh share landing, regardless of chat. Effect deps
    // on pendingShare so re-mount or re-share re-fires.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingShare])

  const slashTrigger = useMemo(
    () => findSlashTrigger(draft, caretPosition),
    [caretPosition, draft],
  )
  const slashKey = slashTrigger
    ? `${slashTrigger.start}:${slashTrigger.end}:${slashTrigger.query}`
    : null
  const slashOpen = !!slashTrigger && slashDismissedKey !== slashKey
  const slashQuery = slashOpen ? slashTrigger.query : ''
  const deferredSlashQuery = useDeferredValue(slashQuery)
  const { data: slashSkills = [], isLoading: slashSkillsLoading } = useSkills(
    deferredSlashQuery,
    500,
  )
  const { data: allSkills = [], isLoading: allSkillsLoading } = useSkills('', 500)
  const { data: skillIndex } = useSkillIndex()
  const quickSkills = useMemo(() => {
    const skillsByName = new Map(allSkills.map((skill) => [skill.name, skill]))
    const usageByName = new Map(
      (skillIndex?.items ?? []).map((item) => [
        item.name,
        {
          lastUsedAt: (item.last_used_at ?? 0) * 1000,
          usageCount: item.usage_count ?? 0,
        },
      ]),
    )
    const selected: Skill[] = []
    const seen = new Set<string>()
    const pushSkill = (skill: Skill | undefined) => {
      if (!skill || seen.has(skill.name)) return
      seen.add(skill.name)
      selected.push(skill)
    }

    for (const name of PINNED_QUICK_SKILL_NAMES) pushSkill(skillsByName.get(name))
    for (const name of recentSkillNames) pushSkill(skillsByName.get(name))

    const backendRecent = allSkills
      .filter((skill) => (usageByName.get(skill.name)?.lastUsedAt ?? 0) > 0)
      .sort((a, b) => {
        const aUsage = usageByName.get(a.name)
        const bUsage = usageByName.get(b.name)
        return (bUsage?.lastUsedAt ?? 0) - (aUsage?.lastUsedAt ?? 0) ||
          (bUsage?.usageCount ?? 0) - (aUsage?.usageCount ?? 0) ||
          a.name.localeCompare(b.name)
      })
    for (const skill of backendRecent) pushSkill(skill)
    for (const skill of allSkills) pushSkill(skill)
    return selected.slice(0, 12)
  }, [allSkills, recentSkillNames, skillIndex?.items])
  const quickSkillsRecentCount = useMemo(() => {
    const recentNames = new Set(recentSkillNames)
    const backendRecentNames = new Set(
      (skillIndex?.items ?? [])
        .filter((item) => !!item.last_used_at)
        .map((item) => item.name),
    )
    return quickSkills.filter((skill) => recentNames.has(skill.name) || backendRecentNames.has(skill.name)).length
  }, [quickSkills, recentSkillNames, skillIndex?.items])
  const commandToken = useMemo(() => commandNameFromDraft(draft), [draft])

  useEffect(() => {
    if (!slashKey && slashDismissedKey) setSlashDismissedKey(null)
  }, [slashDismissedKey, slashKey])

  useEffect(() => {
    if (docCommandName && docCommandName !== commandToken) {
      setDocCommandName(null)
    }
  }, [commandToken, docCommandName])

  useEffect(() => {
    if (slashOpen && quickSkillsOpen) setQuickSkillsOpen(false)
  }, [quickSkillsOpen, slashOpen])

  function rememberRecentSkill(skill: Skill) {
    setRecentSkillNames((prev) => {
      const next = [skill.name, ...prev.filter((name) => name !== skill.name)].slice(0, 12)
      writeRecentSkillNames(next)
      return next
    })
  }

  function insertSkill(skill: Skill, trigger: SlashTrigger | null = null) {
    rememberRecentSkill(skill)
    const text = draftRef.current
    let nextDraft: string
    let nextCaret: number
    if (trigger) {
      const prefix = text.slice(0, trigger.start)
      const suffix = text.slice(trigger.end)
      const replacement = `/${skill.name} `
      nextDraft = `${prefix}${replacement}${suffix.startsWith(' ') ? suffix.slice(1) : suffix}`
      nextCaret = prefix.length + replacement.length
    } else {
      const el = taRef.current
      const start = el?.selectionStart ?? caretPosition
      const end = el?.selectionEnd ?? start
      const prefix = text.slice(0, start)
      const suffix = text.slice(end)
      const leadingSpace = prefix && !/\s$/.test(prefix) ? ' ' : ''
      const trailingSpace = suffix && !/^\s/.test(suffix) ? ' ' : ''
      const replacement = `${leadingSpace}/${skill.name} ${trailingSpace}`
      nextDraft = `${prefix}${replacement}${suffix}`
      nextCaret = prefix.length + replacement.length
    }
    setDraftFast(nextDraft)
    setSlashDismissedKey(null)
    setCaretPosition(nextCaret)
    requestAnimationFrame(() => {
      const el = taRef.current
      if (!el) return
      el.focus()
      el.setSelectionRange(nextCaret, nextCaret)
    })
  }

  function pickSkill(skill: Skill) {
    if (!slashTrigger) return
    insertSkill(skill, slashTrigger)
  }

  function pickQuickSkill(skill: Skill) {
    setQuickSkillsOpen(false)
    insertSkill(skill)
  }

  function toggleQuickSkills(event?: PointerEvent<HTMLButtonElement>) {
    if (event && event.pointerType !== 'mouse') {
      event.preventDefault()
    }
    setAttachMenuOpen(false)
    setQuickSkillsOpen((open) => !open)
  }

  function syncCaret(el: HTMLTextAreaElement | null = taRef.current) {
    setCaretPosition(el?.selectionStart ?? draftRef.current.length)
  }

  function setTextareaValueAndCaret(value: string, caret: number) {
    const el = taRef.current
    if (el) {
      const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set
      if (setter) setter.call(el, value)
      else el.value = value
      el.setSelectionRange(caret, caret)
    }
    setDraftFast(value)
    setCaretPosition(caret)
  }

  function stopControlledDelete() {
    if (controlledDeleteTimerRef.current !== null) {
      window.clearTimeout(controlledDeleteTimerRef.current)
      controlledDeleteTimerRef.current = null
    }
    controlledDeletePointerIdRef.current = null
    controlledDeleteStartedAtRef.current = 0
    syncCaret()
  }

  function runControlledDeleteOnce(): boolean {
    const el = taRef.current
    const selectionStart = el?.selectionStart ?? caretPosition
    const selectionEnd = el?.selectionEnd ?? selectionStart
    const next = deleteComposerControlled({
      value: draftRef.current,
      selectionStart,
      selectionEnd,
      direction: 'backward',
    })
    if (!next.deleted) {
      stopControlledDelete()
      return false
    }
    setTextareaValueAndCaret(next.value, next.caret)
    return true
  }

  function scheduleControlledDeleteTick() {
    if (controlledDeletePointerIdRef.current === null) return
    const heldMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - controlledDeleteStartedAtRef.current
    controlledDeleteTimerRef.current = window.setTimeout(() => {
      controlledDeleteTimerRef.current = null
      if (runControlledDeleteOnce()) {
        scheduleControlledDeleteTick()
      }
    }, controlledDeleteIntervalMs(heldMs))
  }

  function startControlledDelete(event?: PointerEvent<HTMLButtonElement>) {
    if (!draftRef.current || busy) return
    event?.preventDefault()
    if (event) {
      controlledDeletePointerIdRef.current = event.pointerId
      event.currentTarget.setPointerCapture(event.pointerId)
    } else {
      controlledDeletePointerIdRef.current = -1
    }
    if (controlledDeleteTimerRef.current !== null) {
      window.clearTimeout(controlledDeleteTimerRef.current)
      controlledDeleteTimerRef.current = null
    }
    controlledDeleteStartedAtRef.current = typeof performance !== 'undefined' ? performance.now() : Date.now()
    taRef.current?.focus({ preventScroll: true })
    if (!runControlledDeleteOnce()) return
    controlledDeleteTimerRef.current = window.setTimeout(() => {
      controlledDeleteTimerRef.current = null
      if (runControlledDeleteOnce()) {
        scheduleControlledDeleteTick()
      }
    }, CONTROLLED_DELETE_INITIAL_DELAY_MS)
  }

  function onDraftChange(e: ChangeEvent<HTMLTextAreaElement>) {
    setQuickSkillsOpen(false)
    const next = accelerateComposerDelete({
      previousValue: draftRef.current,
      nextValue: e.target.value,
      selectionStart: e.target.selectionStart,
      selectionEnd: e.target.selectionEnd,
      inputType: inputTypeFromNativeEvent(e.nativeEvent),
      isComposing: isComposingNativeInput(e.nativeEvent),
      nowMs: typeof performance !== 'undefined' ? performance.now() : Date.now(),
      state: deleteAccelerationRef.current,
    })
    const listed = next.accelerated
      ? null
      : continueNumberedList({
          previousValue: draftRef.current,
          nextValue: e.target.value,
          selectionStart: e.target.selectionStart,
          selectionEnd: e.target.selectionEnd,
          inputType: inputTypeFromNativeEvent(e.nativeEvent),
        })
    const result = listed?.changed ? listed : next
    if (next.accelerated || listed?.changed) {
      const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set
      if (setter) setter.call(e.target, result.value)
      else e.target.value = result.value
      e.target.setSelectionRange(result.caret, result.caret)
    }
    setDraftFast(result.value)
    setCaretPosition(result.caret)
  }

  function openAttachMenu(event?: PointerEvent<HTMLButtonElement>) {
    if (event && event.pointerType !== 'mouse') {
      event.preventDefault()
    }
    setQuickSkillsOpen(false)
    setAttachMenuOpen(true)
  }

  function preserveComposerActionFocus(event: PointerEvent<HTMLButtonElement>) {
    if (event.pointerType !== 'mouse') {
      event.preventDefault()
    }
  }

  function cancelPendingUpload(clientId: string) {
    const timer = uploadStartTimersRef.current.get(clientId)
    if (timer != null) {
      window.clearTimeout(timer)
      uploadStartTimersRef.current.delete(clientId)
    }
    const controller = uploadAbortRef.current.get(clientId)
    if (!controller) {
      cancelledUploadIdsRef.current.delete(clientId)
      return
    }
    cancelledUploadIdsRef.current.add(clientId)
    controller.abort()
    uploadAbortRef.current.delete(clientId)
  }

  function _kickOffUpload(file: File, entry: PendingAttachment): Promise<void> {
    const controller = new AbortController()
    cancelledUploadIdsRef.current.delete(entry.clientId)
    uploadAbortRef.current.set(entry.clientId, controller)
    const p = (async () => {
      try {
        const compressed = await _compressImageIfBeneficial(file)
        if (controller.signal.aborted || cancelledUploadIdsRef.current.has(entry.clientId)) return
        const fd = new FormData()
        fd.append('file', compressed, compressed.name)
        const resp = await fetch(`/api/chats/${chatId}/upload`, {
          method: 'POST',
          body: fd,
          credentials: 'include',
          signal: controller.signal,
        })
        if (!resp.ok) throw new Error(`upload ${resp.status}`)
        const j = await resp.json()
        if (controller.signal.aborted || cancelledUploadIdsRef.current.has(entry.clientId)) return
        setPending((prev) => prev.map((a) =>
          a.clientId === entry.clientId
            ? {
                ...a,
                uploadedId: String(j.id),
                localPath: String(j.local_path),
                kind: j.kind || a.kind,
                mimeType: j.mime_type || a.mimeType,
                sizeBytes: typeof j.size_bytes === 'number'
                  ? j.size_bytes : a.sizeBytes,
                filename: String(j.filename || a.filename),
                status: 'uploaded',
                error: null,
              }
            : a,
        ))
      } catch (err) {
        if (controller.signal.aborted || cancelledUploadIdsRef.current.has(entry.clientId)) return
        // Eager upload is an optimization only. The raw File remains on
        // the chip and is promoted into the outbox on Send, so a pre-upload
        // failure must not make an otherwise sendable attachment look broken.
        console.warn('background attachment upload failed; will upload from outbox on send:', err)
      } finally {
        uploadAbortRef.current.delete(entry.clientId)
        cancelledUploadIdsRef.current.delete(entry.clientId)
      }
    })()
    uploadPromisesRef.current.add(p)
    void p.finally(() => uploadPromisesRef.current.delete(p))
    return p
  }

  function _scheduleUpload(file: File, entry: PendingAttachment): void {
    // Eager upload is for photos and short clips. A long recording or video
    // uploads once, from the outbox after Send with progress on the bubble;
    // starting it here would compete with every other pick for bandwidth and
    // be cancelled, bytes wasted, as soon as Send is tapped.
    if (file.size > EAGER_UPLOAD_MAX_BYTES) return
    const timer = window.setTimeout(() => {
      uploadStartTimersRef.current.delete(entry.clientId)
      if (!pendingRef.current.some((a) => a.clientId === entry.clientId)) return
      _kickOffUpload(file, entry)
    }, 150)
    uploadStartTimersRef.current.set(entry.clientId, timer)
  }

  /**
   * Stage one file into the pending strip and kick off its upload.
   *
   * Image previews use object URLs so the user sees their picture
   * immediately without round-tripping through the server. The URLs are
   * NOT revoked when the chip clears on send — submit() defers revocation
   * until after the optimistic message bubble has been replaced by the
   * server-fetched one (otherwise the <img src=blob:…> in the bubble
   * resolves to nothing for ~1-2s, which Mark experienced as "the image
   * doesn't attach").
   *
   * The picked File ref lives on the chip so the Edit pencil can re-open
   * the editor without going back to the picker.
   */
  function _stageFile(file: File): string {
    const previewUrl = file.type.startsWith('image/')
      ? URL.createObjectURL(file) : null
    if (previewUrl) objectUrlsRef.current.add(previewUrl)
    // Pasted images often arrive with a generic name like "image.png".
    // Stamp something more chat-friendly so the chip / filename are useful.
    const name = file.name && file.name !== 'image.png'
      ? file.name
      : `clipboard-${new Date().toISOString().replace(/[:.]/g, '-')}.${
          file.type.split('/')[1] || 'bin'
        }`
    const entry: PendingAttachment = {
      clientId: _newClientId(),
      filename: name,
      kind: _kindForFile(file),
      mimeType: file.type || null,
      sizeBytes: file.size,
      previewUrl,
      uploadedId: null,
      localPath: null,
      status: 'ready',
      error: null,
      originalFile: file,
    }
    flushSync(() => {
      setPending((prev) => [...prev, entry])
    })
    _scheduleUpload(file, entry)
    return entry.clientId
  }

  function _cacheRecentImageSoon(file: File): void {
    window.setTimeout(() => {
      void rememberImage(file)
    }, 500)
  }

  /**
   * Stage `files` into the pending strip and start background upload after
   * the ready UI has had a chance to paint.
   * The pencil button on each image chip re-opens the editor when needed,
   * so the default attach path stays instant while editing remains available.
   *
   * Recent-strip caching is scheduled HERE (on pick), not later in _stageFile.
   * Why: the cache must populate even if the user dismisses the edit
   * modal — otherwise the Recent strip stays empty for anyone who
   * habitually previews-then-discards. The cache work is deliberately
   * after staging so IDB/image hashing never delays the ready chip.
   */
  function _processFiles(files: File[]) {
    if (files.length === 0) return
    for (const f of files) {
      _stageFile(f)
      // Cache every picked image to the AttachMenu Recent strip,
      // regardless of whether it ultimately gets staged or sent.
      // Delayed fire-and-forget; the IDB write is internally guarded.
      if (f.type.startsWith('image/')) {
        _cacheRecentImageSoon(f)
      }
    }
  }

  /**
   * Paste-from-clipboard support. Mark's primary fast path on Android:
   * screenshot → long-press composer → Paste → image attached. No file
   * picker, no gallery scroll. WhatsApp-class speed for the everyday
   * "send a screenshot" flow.
   *
   * Falls through to default paste behavior when the clipboard contains
   * only text — preventDefault is conditional on actually finding files.
   */
  function onPaste(e: ClipboardEvent<HTMLTextAreaElement> | globalThis.ClipboardEvent) {
    const items = e.clipboardData?.items
    if (!items) return
    const files: File[] = []
    for (const item of Array.from(items)) {
      if (item.kind !== 'file') continue
      const f = item.getAsFile()
      if (!f) continue
      const t = f.type
      if (
        t.startsWith('image/') ||
        t.startsWith('video/') ||
        t.startsWith('audio/') ||
        t === 'application/pdf'
      ) {
        files.push(f)
      }
    }
    if (files.length === 0) return  // text paste — let default run
    e.preventDefault()
    _processFiles(files)
  }

  function removePending(clientId: string) {
    cancelPendingUpload(clientId)
    setPending((prev) => {
      const removed = prev.find((a) => a.clientId === clientId)
      if (removed?.previewUrl) {
        URL.revokeObjectURL(removed.previewUrl)
        objectUrlsRef.current.delete(removed.previewUrl)
      }
      if (removed) discardUploadedPendingAttachment(removed)
      return prev.filter((a) => a.clientId !== clientId)
    })
  }

  async function submit(force?: boolean) {
    if (busy) return
    const t = draftRef.current.trim()
    // Allow tap when there's text OR any pending attachment, even if it's
    // still uploading — we'll wait internally so the tap feels instant.
    if (!t && pendingRef.current.length === 0) return
    // Always queue by default — per-message Force-now is exposed on each
    // queued bubble in MessageList (see useForceNow).
    const wantForce = force ?? false
    setBusy(true)
    setDraftLocked(true)
    setSendError(null)
    try {
      // Before any await that could be interrupted by reload/app-kill,
      // force the typed text into the normal draft store.
      flushPendingDraftPersist()

      const finalPending = pendingRef.current
      const atts: SendAttachment[] = finalPending
        .map(pendingToOutboxAttachment)
        .filter((att): att is OutboxAttachment => att !== null)
      if (!t && atts.length === 0) return  // every upload errored and no local file remains

      // Capture the blob URLs the optimistic message will reference
      // BEFORE we clear the chips. They must outlive the chip-clear so
      // the in-bubble <img src=blob:…> resolves until the server refetch
      // (triggered by send.onSettled) replaces the temp message with the
      // real /api/attachments/<id>/file URL. Revoking immediately (the
      // old behaviour) was the "image doesn't attach" bug.
      const blobsToRevoke = finalPending
        .map((a) => a.previewUrl)
        .filter((u): u is string => !!u)

      const outboxId = newOutboxId()
      const cursorModelOverride = showCursorModel && cursorModel !== 'auto' ? cursorModel : null
      const cursorEffortOverride = showCursorModel && cursorThinkingAvailable && selectedCursorEffort !== 'auto'
        ? selectedCursorEffort
        : null
      const modelOverride = showClaudeControls
        ? claudeModel
        : showCodexControls
          ? selectedCodexModel
          : cursorModelOverride
      const effortOverride = showClaudeControls
        ? claudeEffort
        : showCodexControls
          ? selectedCodexEffort
          : cursorEffortOverride
      const sendInput: SendMessageInput = {
        text: t,
        force: wantForce,
        topic_id: topicId ?? null,
        model_override: modelOverride,
        effort_override: effortOverride,
        attachments: atts.length > 0 ? atts : undefined,
        client_id: outboxId,
      }
      // Paint first, in this exact tap task. IndexedDB can occasionally take
      // long enough on Android to make the old await-first path look unsent.
      const stagedMessage = stageOutgoingMessage(sendInput)
      try {
        await Promise.all([
          enqueueOutbox(
            chatId,
            t,
            wantForce,
            topicId ?? null,
            atts,
            modelOverride,
            effortOverride,
            outboxId,
          ),
          stagedMessage.ready,
        ])
      } catch (error) {
        // The draft has not been cleared yet. Remove only the tentative row;
        // the existing catch surfaces the storage failure below the composer.
        stagedMessage.rollback()
        throw error
      }
      // Do not wait for the eager pre-upload. The outbox now holds the raw
      // File blobs, so a Send tap is durable immediately and upload can
      // continue from there. Cancel any redundant eager upload after the
      // durable write succeeds to avoid double-staging.
      for (const att of finalPending) {
        if (!att.localPath && att.originalFile) cancelPendingUpload(att.clientId)
      }
      const hasLocalQueuedAttachment = atts.some((att) => !!att.file && !att.local_path)

      // From this point the message is durable in the outbox, so the
      // composer can clear without creating a reload-loss window.
      clearDraftFast()
      clearPersistedPendingAttachments(draftKey)
      setPending([])
      // The outgoing text is durable. Server delivery must not lock the next draft.
      setDraftLocked(false)
      requestAnimationFrame(() => taRef.current?.focus({ preventScroll: true }))

      try {
        await send.mutateAsync(sendInput)
      } catch {
        // Outbox handled this; surface in pill below.
      } finally {
        // Server-fetched message has replaced the optimistic one in cache
        // by now (onSettled invalidate → refetch). 2s buffer absorbs slow
        // refetches on flaky mobile data.
        if (!hasLocalQueuedAttachment) {
          setTimeout(() => {
            for (const url of blobsToRevoke) {
              URL.revokeObjectURL(url)
              objectUrlsRef.current.delete(url)
            }
          }, 2000)
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'send failed'
      setSendError(msg)
    } finally {
      setDraftLocked(false)
      setBusy(false)
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter') {
      // The textarea owns Enter even when command suggestions are visible.
      e.stopPropagation()
      if (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229 || e.shiftKey) return
      if (isMobileDevice && !e.ctrlKey && !e.metaKey) return
      e.preventDefault()
      if (!e.repeat) void submit()
      return
    }
    // Esc closes slash menu (also handled inside SlashMenu but keyboard
    // races caused issues, so we duplicate here).
    if (e.key === 'Escape' && slashOpen) {
      e.preventDefault()
      setSlashDismissedKey(slashKey)
    }
  }

  // ── Recorded dictation ─────────────────────────────────────────────────

  function setDictationStateFast(nextState: DictationState) {
    dictationStateRef.current = nextState
    setDictationState(nextState)
  }

  function setAutoVoiceReplyActive(nextActive: boolean) {
    autoVoiceReplyActiveRef.current = nextActive
    setAutoVoiceReplyActiveState(nextActive)
    if (!nextActive) {
      autoVoiceReplyPendingSendRef.current = false
      autoVoiceReplyStopScheduledRef.current = false
    }
  }

  function finishAutoVoiceReplySession(shouldSend: boolean) {
    setAutoVoiceReplyActive(false)
    if (!shouldSend) return

    const replyText = draftRef.current.trim()
    if (!replyText) {
      setDictationNotice('say your reply before "send reply"')
      return
    }

    setDictationNotice('sending dictated reply...')
    window.setTimeout(() => {
      void submit()
    }, 0)
  }

  function textWithoutAutoVoiceReplyCommand(text: string): string {
    const recognizedText = text.trim()
    if (!autoVoiceReplyActiveRef.current) return recognizedText

    const parsed = parseVoiceReplyCommand(recognizedText)
    if (!parsed.shouldSend) return parsed.text

    autoVoiceReplyPendingSendRef.current = true
    setDictationNotice(parsed.text ? 'sending dictated reply...' : 'say your reply before "send reply"')
    if (!autoVoiceReplyStopScheduledRef.current && dictationStateRef.current === 'listening') {
      autoVoiceReplyStopScheduledRef.current = true
      window.setTimeout(() => stopDictation(), 0)
    }
    return parsed.text
  }

  function resetDictationTextSession() {
    dictationBaseDraftRef.current = draftRef.current
    dictationFinalTextRef.current = ''
    dictationInterimTextRef.current = ''
    dictationLastPartialTextRef.current = ''
    dictationProgressTextRef.current = ''
    dictationRenderedDraftRef.current = draftRef.current
  }

  function syncDictationBaseWithUserEdits() {
    const renderedDraft = dictationRenderedDraftRef.current
    if (draftRef.current === renderedDraft) return
    dictationBaseDraftRef.current = draftRef.current
    dictationFinalTextRef.current = ''
    dictationInterimTextRef.current = ''
    dictationLastPartialTextRef.current = ''
    dictationProgressTextRef.current = ''
    dictationRenderedDraftRef.current = draftRef.current
  }

  function renderDictationDraft({ preserveProgress = true }: { preserveProgress?: boolean } = {}) {
    syncDictationBaseWithUserEdits()
    const proposedRecognizedText = mergeDictationText(
      dictationFinalTextRef.current,
      dictationInterimTextRef.current,
    )
    const recognizedText = preserveProgress
      ? preserveDictationProgress(dictationProgressTextRef.current, proposedRecognizedText)
      : proposedRecognizedText.trimStart()
    dictationProgressTextRef.current = recognizedText
    const nextDraft = appendDictationText(dictationBaseDraftRef.current, recognizedText)
    dictationRenderedDraftRef.current = nextDraft
    if (nextDraft === draftRef.current) return
    setDraftFast(nextDraft, { immediate: true })
    setCaretPosition(nextDraft.length)
  }

  function setPreviewRecognizedText(text: string) {
    const previewText = textWithoutAutoVoiceReplyCommand(text)
    if (!previewText) return
    dictationInterimTextRef.current = previewText
    dictationLastPartialTextRef.current = previewText
    renderDictationDraft({ preserveProgress: true })
    scheduleDictationRecoverySnapshot('recording')
  }

  function replaceWithFinalRecognizedText(text: string) {
    dictationFinalTextRef.current = textWithoutAutoVoiceReplyCommand(text)
    dictationInterimTextRef.current = ''
    dictationLastPartialTextRef.current = ''
    dictationProgressTextRef.current = ''
    renderDictationDraft({ preserveProgress: false })
  }

  function clearDictationStopTimer() {
    if (dictationStopTimerRef.current !== null) {
      window.clearTimeout(dictationStopTimerRef.current)
      dictationStopTimerRef.current = null
    }
    if (dictationHardStopTimerRef.current !== null) {
      window.clearTimeout(dictationHardStopTimerRef.current)
      dictationHardStopTimerRef.current = null
    }
  }

  function preserveRenderedDictationDraft() {
    const renderedDraft = dictationRenderedDraftRef.current
    if (!renderedDraft.trim()) return
    if (renderedDraft.length < draftRef.current.length) return
    if (renderedDraft === draftRef.current) return
    setDraftFast(renderedDraft, { immediate: true })
    setCaretPosition(renderedDraft.length)
  }

  function chooseDictationMimeType(): string {
    if (typeof MediaRecorder === 'undefined') return ''
    for (const mimeType of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']) {
      if (MediaRecorder.isTypeSupported(mimeType)) return mimeType
    }
    return ''
  }

  function stopDictationMediaStream() {
    dictationMediaStreamRef.current?.getTracks().forEach((track) => track.stop())
    dictationMediaStreamRef.current = null
  }

  function dictationFilenameForMime(mimeType: string): string {
    const extension = mimeType.includes('mp4') ? 'mp4' : 'webm'
    return `dictation-${Date.now()}.${extension}`
  }

  function currentDictationBlob(): Blob | null {
    const chunks = dictationRecordingChunksRef.current
    if (chunks.length === 0) return null
    const mimeType = dictationRecorderMimeTypeRef.current || 'audio/webm'
    const blob = new Blob(chunks, { type: mimeType })
    return blob.size > 0 ? blob : null
  }

  function clearDictationRecoverySaveTimer() {
    if (dictationRecoverySaveTimerRef.current !== null) {
      window.clearTimeout(dictationRecoverySaveTimerRef.current)
      dictationRecoverySaveTimerRef.current = null
    }
  }

  function resetDictationRecoveryRefs() {
    clearDictationRecoverySaveTimer()
    dictationRecoveryIdRef.current = null
    dictationRecoveryCreatedAtRef.current = 0
    dictationFallbackAttachmentIdRef.current = null
  }

  async function writeDictationRecoverySnapshot(
    state: DictationRecoveryState,
    reason: string | null = null,
  ) {
    const id = dictationRecoveryIdRef.current
    if (!id) return
    const blob = currentDictationBlob()
    const mimeType = blob?.type || dictationRecorderMimeTypeRef.current || 'audio/webm'
    await saveDictationRecovery({
      id,
      chat_id: chatId,
      topic_id: topicId ?? null,
      draft_key: draftKey,
      base_draft: dictationBaseDraftRef.current,
      body: dictationRenderedDraftRef.current || draftRef.current,
      mime_type: mimeType,
      filename: dictationFilenameForMime(mimeType),
      audio: blob,
      created_at: dictationRecoveryCreatedAtRef.current || Date.now(),
      updated_at: Date.now(),
      state,
      reason,
    })
  }

  function scheduleDictationRecoverySnapshot(
    state: DictationRecoveryState,
    reason: string | null = null,
  ) {
    clearDictationRecoverySaveTimer()
    dictationRecoverySaveTimerRef.current = window.setTimeout(() => {
      dictationRecoverySaveTimerRef.current = null
      void writeDictationRecoverySnapshot(state, reason)
    }, 1000)
  }

  function recordComposerDictationDiagnostic(
    event: Omit<DictationDiagnosticEvent, 'chat_id' | 'topic_id'>,
  ) {
    recordDictationDiagnostic({
      ...event,
      chat_id: chatId,
      topic_id: topicId ?? null,
    })
  }

  function stageDictationAudioFallback(reason: string): boolean {
    if (dictationFallbackAttachmentIdRef.current) return true
    const blob = currentDictationBlob()
    if (!blob) return false
    const mimeType = blob.type || dictationRecorderMimeTypeRef.current || 'audio/webm'
    const filename = dictationFilenameForMime(mimeType)
    const file = new File([blob], filename, { type: mimeType })
    dictationFallbackAttachmentIdRef.current = _stageFile(file)
    void writeDictationRecoverySnapshot('failed', reason)
    recordComposerDictationDiagnostic({
      event: 'error',
      engine: 'openai-transcribe',
      error_code: reason,
      recoverable: true,
    })
    return true
  }

  function clearDictationPreviewTimer() {
    if (dictationPreviewTimerRef.current !== null) {
      window.clearTimeout(dictationPreviewTimerRef.current)
      dictationPreviewTimerRef.current = null
    }
  }

  function stopDictationPreviewLoop({ abortActive }: { abortActive: boolean }) {
    clearDictationPreviewTimer()
    dictationPreviewPendingRef.current = false
    if (abortActive) {
      const abortController = dictationPreviewAbortRef.current
      dictationPreviewAbortRef.current = null
      abortController?.abort()
      dictationPreviewInFlightRef.current = false
    }
  }

  async function postDictationRecording(
    blob: Blob,
    filename: string,
    abortController: AbortController,
  ): Promise<string> {
    if (blob.size <= 0) {
      throw new Error('dictation recorded no audio')
    }
    const formData = new FormData()
    formData.append('audio', blob, filename)
    formData.append('chat_id', chatId)
    if (topicId) formData.append('topic_id', topicId)
    const response = await fetch('/api/voice/dictation', {
      method: 'POST',
      credentials: 'include',
      body: formData,
      signal: abortController.signal,
    })
    if (!response.ok) {
      throw new Error(`dictation transcription failed (${response.status})`)
    }
    const payload = await response.json() as { transcript?: unknown }
    return typeof payload.transcript === 'string' ? payload.transcript : ''
  }

  function scheduleDictationPreviewTranscription() {
    clearDictationPreviewTimer()
    if (
      dictationEngineRef.current !== 'openai-transcribe' ||
      dictationStateRef.current !== 'listening' ||
      dictationFinalizingRef.current
    ) {
      return
    }
    dictationPreviewTimerRef.current = window.setTimeout(() => {
      void transcribeDictationPreview()
    }, DICTATION_PREVIEW_INTERVAL_MS)
  }

  async function transcribeDictationPreview() {
    if (
      dictationEngineRef.current !== 'openai-transcribe' ||
      dictationStateRef.current !== 'listening' ||
      dictationFinalizingRef.current
    ) {
      return
    }
    if (dictationPreviewInFlightRef.current) {
      dictationPreviewPendingRef.current = true
      return
    }
    const chunks = [...dictationRecordingChunksRef.current]
    if (chunks.length === 0) {
      scheduleDictationPreviewTranscription()
      return
    }
    const mimeType = dictationRecorderMimeTypeRef.current || 'audio/webm'
    const sequence = dictationPreviewSequenceRef.current + 1
    dictationPreviewSequenceRef.current = sequence
    const abortController = new AbortController()
    dictationPreviewAbortRef.current = abortController
    dictationPreviewInFlightRef.current = true
    try {
      const blob = new Blob(chunks, { type: mimeType })
      const transcript = await postDictationRecording(
        blob,
        dictationFilenameForMime(mimeType),
        abortController,
      )
      if (
        dictationEngineRef.current !== 'openai-transcribe' ||
        dictationStateRef.current !== 'listening' ||
        sequence < dictationLastAppliedPreviewSequenceRef.current
      ) {
        return
      }
      dictationLastAppliedPreviewSequenceRef.current = sequence
      setPreviewRecognizedText(transcript)
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        recordComposerDictationDiagnostic({
          event: 'error',
          engine: 'openai-transcribe',
          error_code: 'preview_transcription_failed',
          recoverable: true,
        })
      }
    } finally {
      if (dictationPreviewAbortRef.current === abortController) {
        dictationPreviewAbortRef.current = null
      }
      dictationPreviewInFlightRef.current = false
      if (dictationPreviewPendingRef.current) {
        dictationPreviewPendingRef.current = false
        void transcribeDictationPreview()
      } else {
        scheduleDictationPreviewTranscription()
      }
    }
  }

  function cleanupRecordedDictation() {
    stopDictationPreviewLoop({ abortActive: true })
    const abortController = dictationTranscribeAbortRef.current
    dictationTranscribeAbortRef.current = null
    abortController?.abort()
    dictationFinalizingRef.current = false
    const recorder = dictationMediaRecorderRef.current
    dictationMediaRecorderRef.current = null
    if (recorder && recorder.state !== 'inactive') {
      recorder.ondataavailable = null
      recorder.onstop = null
      recorder.onerror = null
      try {
        recorder.stop()
      } catch {
        // Already stopped.
      }
    }
    stopDictationMediaStream()
    dictationRecordingChunksRef.current = []
    dictationRecorderMimeTypeRef.current = 'audio/webm'
    dictationPreviewSequenceRef.current = 0
    dictationLastAppliedPreviewSequenceRef.current = 0
    dictationRecordingStartedAtRef.current = 0
  }

  function cleanupDictation() {
    clearDictationStopTimer()
    preserveRenderedDictationDraft()
    cleanupRecordedDictation()
    dictationEngineRef.current = null
    dictationFinalTextRef.current = ''
    dictationInterimTextRef.current = ''
    dictationLastPartialTextRef.current = ''
    dictationProgressTextRef.current = ''
    dictationRenderedDraftRef.current = ''
    resetDictationRecoveryRefs()
    setDictationNotice(null)
  }

  function failDictation(message: string) {
    setAutoVoiceReplyActive(false)
    cleanupDictation()
    setDictationError(message)
    setDictationStateFast('error')
  }

  async function transcribeDictationRecording(blob: Blob): Promise<boolean> {
    const mimeType = blob.type || dictationRecorderMimeTypeRef.current || 'audio/webm'
    const abortController = new AbortController()
    dictationTranscribeAbortRef.current = abortController
    try {
      const transcript = await postDictationRecording(
        blob,
        dictationFilenameForMime(mimeType),
        abortController,
      )
      if (dictationEngineRef.current !== 'openai-transcribe') return false
      if (!transcript.trim()) {
        preserveRenderedDictationDraft()
        return false
      }
      replaceWithFinalRecognizedText(transcript)
      recordComposerDictationDiagnostic({
        event: 'final_result',
        engine: 'openai-transcribe',
        result_chars: transcript.trim().length,
      })
      return true
    } finally {
      if (dictationTranscribeAbortRef.current === abortController) {
        dictationTranscribeAbortRef.current = null
      }
    }
  }

  async function finalizeRecordedDictation() {
    if (dictationFinalizingRef.current) return
    if (dictationEngineRef.current !== 'openai-transcribe') return
    dictationFinalizingRef.current = true
    stopDictationPreviewLoop({ abortActive: true })
    const elapsedMs = dictationRecordingStartedAtRef.current > 0
      ? Date.now() - dictationRecordingStartedAtRef.current
      : DICTATION_MIN_FINAL_AUDIO_MS
    const mimeType = dictationRecorderMimeTypeRef.current || 'audio/webm'
    const blob = new Blob(dictationRecordingChunksRef.current, { type: mimeType })
    if (elapsedMs < DICTATION_MIN_FINAL_AUDIO_MS) {
      if (blob.size > 0) {
        recordComposerDictationDiagnostic({
          event: 'state',
          engine: 'openai-transcribe',
          state: 'short_audio_transcribing',
        })
      } else {
        recordComposerDictationDiagnostic({
          event: 'state',
          engine: 'openai-transcribe',
          state: 'short_empty_audio',
        })
        const recoveryId = dictationRecoveryIdRef.current
        if (recoveryId) void deleteDictationRecovery(recoveryId)
        const shouldSendAutoVoiceReply =
          autoVoiceReplyActiveRef.current && autoVoiceReplyPendingSendRef.current
        cleanupDictation()
        setDictationStateFast('idle')
        finishAutoVoiceReplySession(shouldSendAutoVoiceReply)
        dictationFinalizingRef.current = false
        return
      }
    }
    if (blob.size <= 0) {
      const recoveryId = dictationRecoveryIdRef.current
      if (recoveryId) void deleteDictationRecovery(recoveryId)
      const shouldSendAutoVoiceReply =
        autoVoiceReplyActiveRef.current && autoVoiceReplyPendingSendRef.current
      cleanupDictation()
      setDictationStateFast('idle')
      finishAutoVoiceReplySession(shouldSendAutoVoiceReply)
      dictationFinalizingRef.current = false
      return
    }
    void writeDictationRecoverySnapshot('finalizing')
    try {
      const appliedTranscript = await transcribeDictationRecording(blob)
      if (dictationEngineRef.current !== 'openai-transcribe') return
      const recoveryId = dictationRecoveryIdRef.current
      const keptPreviewOnly = !appliedTranscript &&
        dictationRenderedDraftRef.current.trim() &&
        dictationRenderedDraftRef.current !== dictationBaseDraftRef.current
      if (recoveryId && !keptPreviewOnly) {
        void deleteDictationRecovery(recoveryId)
      }
      const shouldSendAutoVoiceReply =
        autoVoiceReplyActiveRef.current && autoVoiceReplyPendingSendRef.current
      cleanupDictation()
      setDictationStateFast('idle')
      finishAutoVoiceReplySession(shouldSendAutoVoiceReply)
    } catch (error) {
      if (dictationEngineRef.current !== 'openai-transcribe') return
      const audioKept = stageDictationAudioFallback('final_transcription_failed')
      const message = error instanceof Error ? error.message : 'dictation transcription failed'
      failDictation(audioKept ? `${message}; audio kept as attachment` : message)
    } finally {
      dictationFinalizingRef.current = false
    }
  }

  async function startRecordedTranscriptionDictation() {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      throw new Error('audio recording is not available in this browser')
    }
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    })
    const mimeType = chooseDictationMimeType()
    let recorder: MediaRecorder
    try {
      recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream)
    } catch (error) {
      stream.getTracks().forEach((track) => track.stop())
      throw error
    }
    dictationEngineRef.current = 'openai-transcribe'
    dictationMediaStreamRef.current = stream
    dictationMediaRecorderRef.current = recorder
    dictationRecordingChunksRef.current = []
    dictationRecorderMimeTypeRef.current = recorder.mimeType || mimeType || 'audio/webm'
    dictationFinalizingRef.current = false
    dictationPreviewSequenceRef.current = 0
    dictationLastAppliedPreviewSequenceRef.current = 0
    dictationRecordingStartedAtRef.current = Date.now()
    dictationRecoveryIdRef.current = _newClientId()
    dictationRecoveryCreatedAtRef.current = dictationRecordingStartedAtRef.current
    dictationFallbackAttachmentIdRef.current = null
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        dictationRecordingChunksRef.current.push(event.data)
        scheduleDictationRecoverySnapshot('recording')
      }
    }
    recorder.onerror = () => failDictation('dictation recorder failed')
    recorder.onstop = () => {
      stopDictationMediaStream()
      if (dictationEngineRef.current !== 'openai-transcribe') return
      if (dictationStateRef.current === 'listening') {
        setDictationStateFast('stopping')
      }
      void finalizeRecordedDictation()
    }
    try {
      recorder.start(250)
    } catch (error) {
      cleanupRecordedDictation()
      throw error
    }
    recordComposerDictationDiagnostic({ event: 'start', engine: 'openai-transcribe' })
    setDictationStateFast('listening')
    void writeDictationRecoverySnapshot('recording')
    scheduleDictationPreviewTranscription()
  }

  async function startDictation() {
    if (dictationStateRef.current === 'starting' || dictationStateRef.current === 'listening') {
      return
    }
    setAttachMenuOpen(false)
    setQuickSkillsOpen(false)
    setDictationError(null)
    setDictationNotice(null)
    setDictationPermissionSheetOpen(false)
    clearDictationStopTimer()
    resetDictationRecoveryRefs()
    resetDictationTextSession()
    setDictationStateFast('starting')
    try {
      await startRecordedTranscriptionDictation()
    } catch (error) {
      failDictation(dictationStartErrorMessage(error))
      if (isDictationPermissionError(error)) {
        setDictationPermissionSheetOpen(true)
      }
    }
  }

  function stopDictation() {
    const state = dictationStateRef.current
    if (state === 'idle' || state === 'error') return
    setDictationStateFast('stopping')
    if (dictationEngineRef.current === 'openai-transcribe') {
      recordComposerDictationDiagnostic({ event: 'stop', engine: 'openai-transcribe' })
      clearDictationStopTimer()
      dictationStopTimerRef.current = window.setTimeout(() => {
        if (dictationEngineRef.current !== 'openai-transcribe' || !dictationFinalizingRef.current) return
        preserveRenderedDictationDraft()
        void writeDictationRecoverySnapshot('finalizing', 'final_transcription_slow')
        setDictationNotice('dictation saved; still transcribing...')
        recordComposerDictationDiagnostic({
          event: 'state',
          engine: 'openai-transcribe',
          state: 'final_transcription_slow',
        })
      }, DICTATION_TRANSCRIBE_SLOW_NOTICE_MS)
      dictationHardStopTimerRef.current = window.setTimeout(() => {
        if (dictationEngineRef.current !== 'openai-transcribe' || !dictationFinalizingRef.current) return
        preserveRenderedDictationDraft()
        const audioKept = stageDictationAudioFallback('final_transcription_stalled')
        const abortController = dictationTranscribeAbortRef.current
        dictationTranscribeAbortRef.current = null
        dictationEngineRef.current = null
        abortController?.abort()
        cleanupDictation()
        setAutoVoiceReplyActive(false)
        setDictationError(audioKept
          ? 'transcription stalled; audio kept as attachment'
          : 'transcription stalled; partial draft kept')
        setDictationStateFast('error')
      }, DICTATION_TRANSCRIBE_HARD_TIMEOUT_MS)
      const recorder = dictationMediaRecorderRef.current
      if (recorder && recorder.state !== 'inactive') {
        try {
          recorder.requestData()
        } catch {
          // Some Android WebViews throw if requestData races stop(); stop still emits final data.
        }
        try {
          recorder.stop()
        } catch {
          stopDictationMediaStream()
          void finalizeRecordedDictation()
        }
      } else {
        stopDictationMediaStream()
        void finalizeRecordedDictation()
      }
    } else {
      cleanupDictation()
      setDictationStateFast('idle')
      return
    }
  }

  function toggleDictation() {
    const state = dictationStateRef.current
    if (state === 'starting' || state === 'stopping') return
    if (state === 'idle' || state === 'error') {
      void startDictation()
      return
    }
    stopDictation()
  }

  useEffect(() => {
    if (!autoVoiceReplyRequest) return
    if (autoVoiceReplyRequest.id === autoVoiceReplyRequestIdRef.current) return
    if (
      busy ||
      dictationStateRef.current === 'starting' ||
      dictationStateRef.current === 'listening' ||
      dictationStateRef.current === 'stopping'
    ) {
      setDictationNotice('auto-listen skipped; composer is busy')
      return
    }

    autoVoiceReplyRequestIdRef.current = autoVoiceReplyRequest.id
    autoVoiceReplyPendingSendRef.current = false
    autoVoiceReplyStopScheduledRef.current = false
    setAutoVoiceReplyActive(true)
    void startDictation()
    // startDictation is intentionally not a dependency; request id is the one-shot trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoVoiceReplyRequest?.id, busy])

  function recoveryAudioFile(item: DictationRecoveryItem): File | null {
    if (!item.audio || item.audio.size <= 0) return null
    const mimeType = item.audio.type || item.mime_type || 'audio/webm'
    const filename = item.filename || dictationFilenameForMime(mimeType)
    return new File([item.audio], filename, { type: mimeType })
  }

  async function dismissDictationRecovery(id: string) {
    await deleteDictationRecovery(id)
    setDictationRecoveries((items) => items.filter((item) => item.id !== id))
  }

  function restoreDictationRecoveryText(item: DictationRecoveryItem) {
    if (!item.body.trim()) return
    setDraftFast(item.body, { immediate: true })
    setCaretPosition(item.body.length)
    if (!item.audio || item.audio.size <= 0) {
      void dismissDictationRecovery(item.id)
    }
  }

  function attachRecoveredDictationAudio(item: DictationRecoveryItem) {
    const file = recoveryAudioFile(item)
    if (!file) return
    _stageFile(file)
    void dismissDictationRecovery(item.id)
  }

  async function retryRecoveredDictation(item: DictationRecoveryItem) {
    const file = recoveryAudioFile(item)
    if (!file || dictationRecoveryBusyId) return
    const abortController = new AbortController()
    const timeoutId = window.setTimeout(() => {
      abortController.abort()
    }, DICTATION_TRANSCRIBE_HARD_TIMEOUT_MS)
    setDictationRecoveryBusyId(item.id)
    setDictationError(null)
    setDictationNotice('retrying recovered dictation...')
    try {
      const transcript = await postDictationRecording(file, file.name, abortController)
      const recoveredText = transcript.trim()
      if (recoveredText) {
        const base = item.base_draft || ''
        const nextDraft = appendDictationText(base, recoveredText)
        setDraftFast(nextDraft, { immediate: true })
        setCaretPosition(nextDraft.length)
        await dismissDictationRecovery(item.id)
        return
      }
      if (item.body.trim()) restoreDictationRecoveryText(item)
    } catch (error) {
      setDictationError(error instanceof Error ? error.message : 'recovered dictation retry failed')
    } finally {
      window.clearTimeout(timeoutId)
      setDictationRecoveryBusyId(null)
      setDictationNotice(null)
    }
  }

  // ── Voice recorder ────────────────────────────────────────────────────

  async function startRecording() {
    if (recording) return
    clearRecorderErrors()
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      recStreamRef.current = stream
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' })
      recRecorderRef.current = rec
      recChunksRef.current = []
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) recChunksRef.current.push(e.data)
      }
      rec.start(250)
      setRecSeconds(0)
      setRecording(true)
      recTickRef.current = window.setInterval(() => setRecSeconds((s) => s + 1), 1000)
    } catch (e) {
      console.error('mic error:', e)
      recStreamRef.current?.getTracks().forEach((t) => t.stop())
      recStreamRef.current = null
      if (recTickRef.current) {
        window.clearInterval(recTickRef.current)
        recTickRef.current = null
      }
      let msg = 'Microphone unavailable'
      if (e instanceof Error) {
        if (e.name === 'NotAllowedError') msg = 'Microphone access denied'
        else if (e.name === 'NotFoundError') msg = 'No microphone found'
        else if (e.name === 'NotSupportedError') {
          msg = 'Voice recording not supported on this device'
        } else msg = e.message || msg
      }
      setMicError(msg)
    }
  }

  async function stopRecordingAndSend() {
    if (!recording || !recRecorderRef.current) return
    setVoiceError(null)
    if (recTickRef.current) {
      window.clearInterval(recTickRef.current)
      recTickRef.current = null
    }
    await new Promise<void>((resolve) => {
      const r = recRecorderRef.current!
      r.onstop = () => resolve()
      r.stop()
    })
    recStreamRef.current?.getTracks().forEach((t) => t.stop())
    setRecording(false)

    const blob = new Blob(recChunksRef.current, { type: 'audio/webm' })
    setVoiceUploading(true)
    try {
      const fd = new FormData()
      fd.append('audio', blob, `voice-${Date.now()}.webm`)
      const resp = await fetch(`/api/chats/${chatId}/voice`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
      })
      if (!resp.ok) throw new Error(`voice upload ${resp.status}`)
    } catch (e) {
      console.error('voice send error:', e)
      setVoiceError(e instanceof Error ? e.message : 'Voice upload failed')
    } finally {
      setVoiceUploading(false)
    }
  }

  function cancelRecording() {
    if (recTickRef.current) {
      window.clearInterval(recTickRef.current)
      recTickRef.current = null
    }
    if (recRecorderRef.current && recRecorderRef.current.state !== 'inactive') {
      try { recRecorderRef.current.stop() } catch {/* ignore */}
    }
    recStreamRef.current?.getTracks().forEach((t) => t.stop())
    recChunksRef.current = []
    setRecording(false)
    setRecSeconds(0)
  }

  // Cleanup on unmount.
  useEffect(() => () => {
    cancelRecording()
    if (dictationEngineRef.current === 'openai-transcribe') {
      preserveRenderedDictationDraft()
      void writeDictationRecoverySnapshot('failed', 'composer_unmounted')
    }
    cleanupDictation()
    for (const timer of uploadStartTimersRef.current.values()) window.clearTimeout(timer)
    uploadStartTimersRef.current.clear()
    for (const controller of uploadAbortRef.current.values()) controller.abort()
    uploadAbortRef.current.clear()
    cancelledUploadIdsRef.current.clear()
    // Revoke any blob URLs still alive — the deferred-revoke in submit()
    // can leave entries in objectUrlsRef if the user navigates away
    // before the 2s buffer elapses.
    for (const url of objectUrlsRef.current) URL.revokeObjectURL(url)
    objectUrlsRef.current.clear()
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Edit modal wiring ────────────────────────────────────────────────
  // Two entry points feed the same modal:
  //   1. Explicit pre-stage edit flow → editQueue[0].
  //   2. Pencil button on a staged chip → editingChipId.
  // editQueue takes precedence so an explicit pre-stage edit always lands first.
  const editingChip = editingChipId
    ? pending.find((a) => a.clientId === editingChipId) ?? null
    : null
  const editingFile: File | null =
    editQueue[0] ?? editingChip?.originalFile ?? null
  const editingMode: 'fresh' | 'chip' | null =
    editQueue.length > 0 ? 'fresh' : editingChip ? 'chip' : null
  const dictationActive =
    dictationState === 'starting' || dictationState === 'listening' || dictationState === 'stopping'
  const dictationChanging = dictationState === 'starting' || dictationState === 'stopping'
  const dictationButtonLabel = dictationActive ? 'Stop dictation' : 'Dictate message'
  const dictationStatusText =
    dictationState === 'starting'
      ? autoVoiceReplyActive ? 'starting voice reply...' : 'starting dictation...'
      : dictationState === 'listening'
        ? autoVoiceReplyActive ? 'listening for reply; say "send reply" to send' : 'dictating...'
        : dictationState === 'stopping'
          ? autoVoiceReplyActive ? 'sending voice reply...' : 'finalising dictation...'
          : null

  function _onEditSave(edited: File): void {
    if (editingMode === 'fresh') {
      _stageFile(edited)
      setEditQueue((q) => q.slice(1))
      return
    }
    if (editingMode === 'chip' && editingChipId) {
      removePending(editingChipId)
      _stageFile(edited)
      setEditingChipId(null)
    }
  }
  function _onEditSkip(): void {
    if (editingMode === 'fresh' && editQueue[0]) {
      // "Use original" on a fresh pick → still stage it (just unedited).
      _stageFile(editQueue[0])
      setEditQueue((q) => q.slice(1))
      return
    }
    // Chip pencil → close without re-staging (the existing chip stays).
    setEditingChipId(null)
  }
  function _onEditCancel(): void {
    if (editingMode === 'fresh') {
      // Discard the picked file entirely — never staged.
      setEditQueue((q) => q.slice(1))
      return
    }
    setEditingChipId(null)
  }

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div
      className="border-t border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900 composer-pad relative"
      onMouseDown={(e) => {
        const t = e.target as HTMLElement
        if (!t.closest('a, button, input, textarea, select, [role="button"], [contenteditable]')) {
          e.preventDefault()
        }
      }}
    >
      {editingFile && (
        <Suspense fallback={null}>
          <ImageEditModal
            key={editingMode === 'fresh'
              ? `fresh-${editingFile.name}-${editingFile.size}-${editingFile.lastModified}`
              : (editingChipId ?? 'chip')}
            file={editingFile}
            onSave={_onEditSave}
            onSkip={_onEditSkip}
            onCancel={_onEditCancel}
          />
        </Suspense>
      )}
      <AttachMenu
        open={attachMenuOpen}
        onPick={(files) => _processFiles(files)}
        onClose={() => setAttachMenuOpen(false)}
      />
      {slashOpen && (
        <SlashMenu
          enterSends={!isMobileDevice}
          query={deferredSlashQuery}
          skills={slashSkills}
          isLoading={slashSkillsLoading}
          onPick={pickSkill}
          onClose={() => setSlashDismissedKey(slashKey)}
        />
      )}
      {quickSkillsOpen && !slashOpen && (
        <SlashMenu
          enterSends={!isMobileDevice}
          query=""
          skills={quickSkills}
          isLoading={allSkillsLoading && quickSkills.length === 0}
          onPick={pickQuickSkill}
          onClose={() => setQuickSkillsOpen(false)}
          title={quickSkillsRecentCount > 0 ? 'Recent Skills' : 'Skills'}
          subtitle={quickSkillsRecentCount > 0 ? 'Most used first' : 'Quick pick'}
          emptyText="No skills found."
          showCloseButton
        />
      )}
      {docCommandName && (
        <CommandDocSheet
          skillName={docCommandName}
          onClose={() => setDocCommandName(null)}
        />
      )}

      {(micError || voiceError) && (
        <div className="flex items-center justify-between gap-2 px-3 pb-1 text-xs text-red-500">
          <span className="min-w-0 flex-1">{micError || voiceError}</span>
          <button
            type="button"
            onClick={clearRecorderErrors}
            className="grid h-5 w-5 shrink-0 place-items-center rounded text-red-500/80 hover:bg-red-500/10 hover:text-red-600"
            aria-label="Dismiss voice error"
            title="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {recording ? (
        // Active recording UI — instead of swapping to a modal we render
        // the controls inline so the chat context stays visible.
        <div className="flex items-center gap-3 px-3 py-2.5">
          <div className="grid place-items-center w-10 h-10 rounded-full bg-red-600 text-white animate-pulse">
            <Mic className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium">Recording…</div>
            <div className="text-xs text-stone-500 tabular-nums">
              {Math.floor(recSeconds / 60).toString().padStart(2, '0')}:
              {(recSeconds % 60).toString().padStart(2, '0')}
            </div>
          </div>
          <button
            onClick={cancelRecording}
            className="grid place-items-center w-10 h-10 rounded-xl bg-stone-200 dark:bg-stone-800 hover:bg-stone-300 dark:hover:bg-stone-700"
            aria-label="Cancel recording"
            title="Cancel"
          >
            <X className="w-4 h-4" />
          </button>
          <button
            onClick={stopRecordingAndSend}
            className="grid place-items-center w-10 h-10 rounded-xl bg-blue-600 text-white hover:bg-blue-700"
            aria-label="Send voice note"
            title="Send"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <>
          {commandToken && (
            <div className="px-3 pt-2">
              <button
                type="button"
                onClick={() => setDocCommandName(commandToken)}
                className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs text-blue-800 shadow-sm dark:border-blue-900/60 dark:bg-blue-950/50 dark:text-blue-200"
                title="View command doc"
              >
                <span className="font-mono truncate">/{commandToken}</span>
                <Info className="w-3.5 h-3.5 shrink-0" />
              </button>
            </div>
          )}
          {/* Pending-attachment preview strip. Image previews show as
              actual thumbnails so Mark sees what he's sending while
              upload is in flight. PDFs / audio / etc. show as compact
              icon chips. Each chip has progress + an X to remove. */}
          {pending.length > 0 && (
            <div
              className="flex gap-2 overflow-x-auto px-3 pt-2 pb-1"
              role="list"
              aria-label="Pending attachments"
            >
              {pending.map((a) => (
                <PendingAttachmentChip
                  key={a.clientId}
                  att={a}
                  onRemove={() => removePending(a.clientId)}
                  onEdit={a.kind === 'image' && a.originalFile
                    ? () => setEditingChipId(a.clientId)
                    : undefined}
                />
              ))}
            </div>
          )}

          {draftConflict && (
            <div role="alert" className="px-3 pt-2 text-xs text-amber-700 dark:text-amber-300">
              Another device changed this draft. Your typing is kept here.
              <div className="flex gap-3 py-1">
                <button type="button" onClick={() => { flushPendingDraftPersist(); resolveDraftConflict(draftKey, 'local') }}>Keep mine</button>
                <button type="button" onClick={() => { flushPendingDraftPersist(); resolveDraftConflict(draftKey, 'shared') }}>Use shared draft</button>
              </div>
            </div>
          )}
          {draftSyncOffline && (
            <div role="status" className="px-3 pt-2 text-xs text-stone-500">Draft saved on this device. Sync will retry when connected.</div>
          )}
          {dictationRecoveries.length > 0 && (
            <div className="space-y-2 px-3 pt-2">
              {dictationRecoveries.slice(0, 2).map((item) => {
                const busyRecovery = dictationRecoveryBusyId === item.id
                const hasAudio = !!item.audio && item.audio.size > 0
                return (
                  <div
                    key={item.id}
                    className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs text-amber-950 shadow-sm dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
                    role="alert"
                    aria-label="Recovered dictation"
                  >
                    <div className="flex items-start gap-2">
                      <FileAudio className="mt-0.5 h-4 w-4 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">Recovered dictation</div>
                        {item.body.trim() && (
                          <div className="mt-1 max-h-10 overflow-hidden break-words opacity-85">
                            {item.body.trim()}
                          </div>
                        )}
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {item.body.trim() && (
                            <button
                              type="button"
                              onClick={() => restoreDictationRecoveryText(item)}
                              className="rounded-md bg-amber-700 px-2 py-1 text-white hover:bg-amber-800"
                            >
                              Restore text
                            </button>
                          )}
                          {hasAudio && (
                            <button
                              type="button"
                              onClick={() => void retryRecoveredDictation(item)}
                              disabled={!!dictationRecoveryBusyId}
                              className="inline-flex items-center gap-1 rounded-md bg-blue-700 px-2 py-1 text-white hover:bg-blue-800 disabled:opacity-60"
                            >
                              {busyRecovery && <Loader2 className="h-3 w-3 animate-spin" />}
                              Retry
                            </button>
                          )}
                          {hasAudio && (
                            <button
                              type="button"
                              onClick={() => attachRecoveredDictationAudio(item)}
                              className="rounded-md bg-stone-800 px-2 py-1 text-white hover:bg-stone-950 dark:bg-stone-200 dark:text-stone-950 dark:hover:bg-white"
                            >
                              Attach audio
                            </button>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => void dismissDictationRecovery(item.id)}
                        className="grid h-6 w-6 shrink-0 place-items-center rounded-full hover:bg-amber-200/70 dark:hover:bg-amber-900"
                        aria-label="Dismiss recovered dictation"
                        title="Dismiss"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {(showCursorModel || showCodexControls || showClaudeControls) && (
            <div className="flex flex-wrap items-center justify-end gap-2 px-3 pt-2">
              <label className="inline-flex max-w-full items-center gap-1.5 text-[11px] text-stone-500 dark:text-stone-400">
                <Cpu className="h-3.5 w-3.5 shrink-0" />
                <span className="shrink-0">Model</span>
                <select
                  value={showCursorModel ? cursorModel : showCodexControls ? selectedCodexModel : claudeModel}
                  onChange={(event) => {
                    if (showCursorModel) {
                      setCursorModel(event.target.value)
                    } else if (showCodexControls) {
                      setCodexModel(event.target.value)
                    } else {
                      setClaudeModel(event.target.value)
                    }
                  }}
                  className="h-7 max-w-[190px] rounded-md border border-stone-200 bg-white px-2 text-xs text-stone-700 outline-none focus:ring-2 focus:ring-blue-500 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-100"
                  aria-label={showCursorModel ? 'Cursor model' : showCodexControls ? 'Codex model' : 'Claude model'}
                >
                  {(showCursorModel ? CURSOR_MODEL_OPTIONS : showCodexControls ? availableCodexModelOptions : CLAUDE_MODEL_OPTIONS).map((option) => (
                    <option key={option.value} value={option.value} disabled={'disabled' in option && !!option.disabled}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              {showCursorModel && (
                <label className="inline-flex max-w-full items-center gap-1.5 text-[11px] text-stone-500 dark:text-stone-400">
                  <Cpu className="h-3.5 w-3.5 shrink-0" />
                  <span className="shrink-0">Thinking</span>
                  <select
                    value={cursorThinkingAvailable ? selectedCursorEffort : 'auto'}
                    onChange={(event) => setCursorEffort(event.target.value)}
                    className="h-7 max-w-[150px] rounded-md border border-stone-200 bg-white px-2 text-xs text-stone-700 outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-100"
                    aria-label="Cursor thinking level"
                    disabled={!cursorThinkingAvailable}
                  >
                    {availableCursorEffortOptions.map((option) => (
                      <option key={option.value} value={option.value} disabled={'disabled' in option && !!option.disabled}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {showCodexControls && (
                <label className="inline-flex max-w-full items-center gap-1.5 text-[11px] text-stone-500 dark:text-stone-400">
                  <Cpu className="h-3.5 w-3.5 shrink-0" />
                  <span className="shrink-0">Thinking</span>
                  <select
                    value={selectedCodexEffort}
                    onChange={(event) => setCodexEffort(event.target.value)}
                    className="h-7 max-w-[150px] rounded-md border border-stone-200 bg-white px-2 text-xs text-stone-700 outline-none focus:ring-2 focus:ring-blue-500 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-100"
                    aria-label="Codex thinking level"
                  >
                    {availableCodexEffortOptions.map((option) => (
                      <option key={option.value} value={option.value} disabled={'disabled' in option && !!option.disabled}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {showClaudeControls && (
                <label className="inline-flex max-w-full items-center gap-1.5 text-[11px] text-stone-500 dark:text-stone-400">
                  <Cpu className="h-3.5 w-3.5 shrink-0" />
                  <span className="shrink-0">Thinking</span>
                  <select
                    value={claudeEffort}
                    onChange={(event) => setClaudeEffort(event.target.value)}
                    className="h-7 max-w-[150px] rounded-md border border-stone-200 bg-white px-2 text-xs text-stone-700 outline-none focus:ring-2 focus:ring-blue-500 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-100"
                    aria-label="Claude thinking level"
                  >
                    {CLAUDE_EFFORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value} disabled={'disabled' in option && !!option.disabled}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}
          {runtimeDivergence && (
            <div
              className="mx-3 mt-1.5 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-[11px] leading-snug text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
              role="status"
              data-testid="runtime-divergence"
            >
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 flex-1">
                {runtimeDivergence.restartsSession
                  ? `Last turn ran ${runtimeDivergence.lastTurnLabel}. Sending as ${runtimeDivergence.selectedLabel} normally starts a fresh session and replays this whole chat. Active CAD controllers keep their working session.`
                  : `Last turn ${topicId ? 'in this topic ' : ''}ran ${runtimeDivergence.lastTurnLabel}; this send uses ${runtimeDivergence.selectedLabel}.`}
              </span>
              {runtimeDivergence.canRevert && (
                <button
                  type="button"
                  // Keep the textarea focused for every pointer type. The
                  // composer padding is focus-dependent (`.composer-pad:has(
                  // textarea:focus)` in index.css), so letting this button
                  // take focus shrinks the composer between pointerdown and
                  // pointerup and the click lands on the composer background
                  // instead of the button — the revert silently did nothing
                  // whenever Mark tapped it mid-typing (headless proof
                  // 2026-09-05). Keyboard activation is unaffected.
                  onPointerDown={(event) => event.preventDefault()}
                  onClick={revertToLastTurn}
                  className="shrink-0 rounded-md border border-amber-400 px-2 py-0.5 font-medium hover:bg-amber-100 dark:border-amber-700 dark:hover:bg-amber-900"
                  aria-label="Use the model and thinking from the last turn"
                >
                  Use last
                </button>
              )}
            </div>
          )}

        <div className="flex items-end gap-2 p-2">
          {/* Single rounded "chat box" wrapping the textarea + a leading
              attachment button — this gives the WhatsApp/Telegram look
              where the textarea is part of a soft pill, with the action
              button OUTSIDE on the right. */}
          <div
            className={clsx(
              'min-w-0 flex-1 flex items-end gap-1 rounded-3xl border transition-colors',
              'bg-white dark:bg-stone-800 border-stone-200 dark:border-stone-700',
              'focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/30',
              'pr-2 pl-1 py-1',
            )}
          >
            <div
              className={clsx(
                'shrink-0 flex',
                composerMultiline
                  ? 'w-7 self-stretch flex-col items-center justify-center gap-0.5'
                  : 'items-end gap-0',
              )}
            >
              <button
                aria-label="Attach"
                onPointerDown={openAttachMenu}
                onClick={() => {
                  setQuickSkillsOpen(false)
                  setAttachMenuOpen(true)
                }}
                className={clsx(
                  'grid place-items-center rounded-full text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors shrink-0',
                  composerMultiline ? 'order-3 w-7 h-7' : 'w-7 h-9',
                )}
                title="Attach"
              >
                <Paperclip className="w-4 h-4" />
              </button>
              <button
                type="button"
                aria-label="Recent skills"
                onPointerDown={(event) => {
                  if (event.pointerType !== 'mouse') event.preventDefault()
                }}
                onClick={() => toggleQuickSkills()}
                className={clsx(
                  'grid place-items-center rounded-full transition-colors shrink-0',
                  composerMultiline ? 'order-2 w-7 h-7' : 'w-7 h-9',
                  quickSkillsOpen
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-200'
                    : 'text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-700',
                )}
                title="Recent skills"
              >
                <Sparkles className="w-4 h-4" />
              </button>
              {composerDeleteVisible ? (
                <button
                  key="controlled-delete"
                  type="button"
                  aria-label="Delete character"
                  disabled={busy}
                  title="Hold to delete faster"
                  onPointerDown={startControlledDelete}
                  onPointerUp={(event) => {
                    if (controlledDeletePointerIdRef.current === event.pointerId) {
                      stopControlledDelete()
                    }
                  }}
                  onPointerCancel={stopControlledDelete}
                  onLostPointerCapture={stopControlledDelete}
                  onContextMenu={(event) => event.preventDefault()}
                  onClick={(event) => event.preventDefault()}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      void runControlledDeleteOnce()
                    }
                  }}
                  className={clsx(
                    'grid place-items-center rounded-full transition-colors shrink-0 active:scale-95 select-none touch-none',
                    'order-1 w-7 h-7',
                    'text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-700',
                    busy && 'disabled:text-stone-300 dark:disabled:text-stone-600 disabled:cursor-not-allowed',
                  )}
                >
                  <DeleteIcon className="w-4 h-4" />
                </button>
              ) : null}
            </div>
            <ComposerTextInput
              ref={taRef}
              scale={textSizeScale}
              placeholder="Message…"
              enterKeyHint={isMobileDevice ? 'enter' : 'send'}
              rows={1}
              value={draft}
              onChange={onDraftChange}
              onSelect={(e) => syncCaret(e.currentTarget)}
              onClick={(e) => syncCaret(e.currentTarget)}
              onKeyUp={(e) => syncCaret(e.currentTarget)}
              onFocus={(e) => {
                syncCaret(e.currentTarget)
                window.dispatchEvent(new CustomEvent('agentchat:composer-focus'))
              }}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              disabled={draftLocked}
              /* No HTML autoFocus — see the smart-keyboard useEffect above. */
            />
          </div>

          <div
            className={clsx(
              'shrink-0 flex',
              composerActionsStacked
                ? 'flex-col items-center justify-end gap-2'
                : 'items-end gap-2',
            )}
          >
            <button
              key="dictation"
              type="button"
              aria-label={dictationButtonLabel}
              aria-pressed={dictationActive}
              disabled={busy || dictationChanging}
              title={dictationActive ? 'Stop dictation' : 'Dictate text'}
              onPointerDown={(event) => {
                if (event.pointerType !== 'mouse') {
                  event.preventDefault()
                }
              }}
              onClick={toggleDictation}
              className={clsx(
                'grid place-items-center w-11 h-11 rounded-full transition-all duration-150 shadow-md shrink-0 active:scale-95',
                dictationActive
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700',
                (busy || dictationChanging) && 'disabled:bg-stone-300 dark:disabled:bg-stone-700 disabled:text-stone-400 disabled:cursor-not-allowed',
              )}
            >
              {dictationChanging ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <TextCursorInput className="w-4 h-4" />
              )}
            </button>

            {/* Single morphing action button OUTSIDE the chat box on the right.
                Empty composer (no text AND no attachments) → Mic.
                Anything to send (text OR pending attachments) → Send.
                The button keeps the same position so the morph reads as
                a transform, not a swap.
                Bug fixed: previously the conditional was ``draft.trim()``
                only, so attaching a photo without typing showed the mic
                button and Mark had no way to send the photo. */}
            {(draft.trim() || pending.length > 0) ? (
              <button
                key="send"
                aria-label="Send"
                disabled={busy || dictationActive}
                title={
                  dictationActive
                    ? 'Stop dictation before sending'
                    : busy
                    ? 'Sending…'
                    : attachmentsUploading
                      ? 'Send now — upload continues or replays from outbox'
                      : 'Send'
                }
                onPointerDown={preserveComposerActionFocus}
                onClick={() => void submit()}
                className="grid place-items-center w-11 h-11 rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:bg-stone-300 dark:disabled:bg-stone-700 disabled:text-stone-400 disabled:cursor-not-allowed transition-all duration-150 shadow-md shrink-0 active:scale-95"
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 -ml-0.5" />
                )}
              </button>
            ) : (
              <button
                key="mic"
                aria-label="Record voice note"
                onPointerDown={preserveComposerActionFocus}
                onClick={() => void startRecording()}
                className="grid place-items-center w-11 h-11 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-all duration-150 shadow-md shrink-0 active:scale-95"
                title="Record voice note (tap to start)"
              >
                <Mic className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        </>
      )}

      {(send.isPending || uploading || send.isError || sendError || dictationStatusText || dictationNotice || dictationError) && (
        <div className="px-3 pb-1.5 text-[11px] flex items-center gap-3 text-stone-500">
          {send.isPending && <span>sending…</span>}
          {uploading && <span>uploading…</span>}
          {dictationStatusText && <span>{dictationStatusText}</span>}
          {dictationNotice && (
            <span className="text-amber-600 dark:text-amber-400">
              {dictationNotice}
            </span>
          )}
          {dictationError && (
            <span className="text-red-500">
              dictation: {dictationError}
            </span>
          )}
          {sendError && (
            <span className="text-red-500">
              not sent: {sendError}
            </span>
          )}
          {send.isError && (
            <span className="text-red-500">
              send failed: {String((send.error as Error).message)}
            </span>
          )}
        </div>
      )}
      {dictationPermissionSheetOpen && (
        <DictationPermissionSheet
          onClose={() => setDictationPermissionSheetOpen(false)}
          onRetry={() => {
            setDictationPermissionSheetOpen(false)
            void startDictation()
          }}
        />
      )}
    </div>
  )
}

function DictationPermissionSheet({
  onClose,
  onRetry,
}: {
  onClose: () => void
  onRetry: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/35" role="dialog" aria-modal="true" aria-labelledby="dictation-permission-title">
      <div className="w-full rounded-t-2xl bg-white text-stone-950 shadow-2xl dark:bg-stone-900 dark:text-stone-50">
        <div className="flex items-center gap-2 border-b border-stone-200 px-4 py-3 dark:border-stone-800">
          <AlertCircle className="h-5 w-5 shrink-0 text-amber-500" />
          <div className="min-w-0 flex-1">
            <div id="dictation-permission-title" className="text-sm font-semibold">
              Enable microphone
            </div>
            <div className="text-[11px] text-stone-500">
              Android or Chrome is blocking AgentChat dictation.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-full text-stone-500 hover:bg-stone-100 dark:hover:bg-stone-800"
            aria-label="Close microphone help"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 px-4 py-4 text-sm text-stone-700 dark:text-stone-200">
          <p>
            Set Microphone to Allow for AgentChat, then return here and try dictation again.
            If Microphone is missing, install the latest AgentChat APK first.
          </p>
          <ol className="list-decimal space-y-1 pl-5 text-[13px] text-stone-600 dark:text-stone-300">
            <li>Open Android Settings -&gt; Apps -&gt; AgentChat.</li>
            <li>Open Permissions -&gt; Microphone.</li>
            <li>Choose Allow.</li>
          </ol>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onRetry}
              className="min-h-11 flex-1 rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 active:scale-[0.99]"
            >
              Try microphone again
            </button>
            <button
              type="button"
              onClick={onClose}
              className="min-h-11 rounded-full px-4 py-2 text-sm font-medium text-stone-600 hover:bg-stone-100 dark:text-stone-300 dark:hover:bg-stone-800"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function CommandDocSheet({
  skillName,
  onClose,
}: {
  skillName: string
  onClose: () => void
}) {
  const { data, isLoading, isError, error } = useSkillDoc(skillName)
  const [tab, setTab] = useState<'summary' | 'full'>('summary')

  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/35" role="dialog" aria-modal="true">
      <div className="w-full max-h-[78vh] overflow-hidden rounded-t-2xl bg-white text-stone-950 shadow-2xl dark:bg-stone-900 dark:text-stone-50">
        <div className="flex items-center gap-2 border-b border-stone-200 px-3 py-2 dark:border-stone-800">
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">
              /{data?.name ?? skillName}
            </div>
            <div className="truncate text-[11px] text-stone-500">
              {data?.path ?? 'Command documentation'}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-full text-stone-500 hover:bg-stone-100 dark:hover:bg-stone-800"
            aria-label="Close command doc"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-1 border-b border-stone-200 p-1 dark:border-stone-800">
          {(['summary', 'full'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              className={clsx(
                'rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                tab === value
                  ? 'bg-blue-600 text-white'
                  : 'text-stone-500 hover:bg-stone-100 dark:hover:bg-stone-800',
              )}
            >
              {value === 'summary' ? 'Summary' : 'Full doc'}
            </button>
          ))}
        </div>

        <div className="max-h-[56vh] overflow-y-auto px-3 py-3 text-sm">
          {isLoading && <div className="text-stone-500">Loading…</div>}
          {isError && (
            <div className="text-red-600">
              {error instanceof Error ? error.message : 'Could not load command doc'}
            </div>
          )}
          {data && tab === 'summary' && (
            <div className="space-y-3">
              {data.description && (
                <p className="text-stone-600 dark:text-stone-300">{data.description}</p>
              )}
              <ul className="space-y-2">
                {data.summary.map((line) => (
                  <li key={line} className="rounded-lg bg-stone-100 px-3 py-2 dark:bg-stone-800">
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data && tab === 'full' && (
            <div className="text-[13px]">
              <MarkdownBody body={data.document} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}


function PendingAttachmentChip({
  att, onRemove, onEdit,
}: {
  att: PendingAttachment
  onRemove: () => void
  onEdit?: () => void
}) {
  // Image attachments render as a 56×56 thumbnail with progress overlay
  // — much nicer than a "[attached image.jpg]" text line. Other kinds
  // get a small icon chip with the filename and a status indicator.
  const isImage = att.kind === 'image' && !!att.previewUrl
  const Icon =
    att.kind === 'pdf' || att.kind === 'document' ? FileText :
    att.kind === 'video' ? VideoIcon :
    att.kind === 'audio' || att.kind === 'voice' ? FileAudio :
    FileIcon
  const sizeLabel = att.sizeBytes != null
    ? `${Math.max(1, Math.round(att.sizeBytes / 1024))} KB`
    : null

  return (
    <div
      role="listitem"
      className={clsx(
        'relative shrink-0 rounded-xl border bg-white dark:bg-stone-800',
        'border-stone-200 dark:border-stone-700 shadow-sm overflow-hidden',
        // Bumped up from 56×56 → 64×64 so the Edit pencil + Remove X
        // both sit comfortably as proper touch targets without
        // overlapping the thumbnail.
        isImage ? 'w-16 h-16' : 'flex items-center gap-2 px-2 py-1.5 max-w-[220px]',
        att.status === 'error' && 'border-red-300 dark:border-red-700',
      )}
    >
      {isImage ? (
        <>
          <img
            src={att.previewUrl!}
            alt={att.filename}
            className="w-full h-full object-cover"
          />
          {(att.status === 'compressing' || att.status === 'uploading') && (
            <div className="absolute top-0.5 left-0.5 grid place-items-center w-5 h-5 rounded-full bg-stone-900/80">
              <Loader2 className="w-3 h-3 text-white animate-spin" />
            </div>
          )}
          {att.status === 'error' && (
            <div className="absolute inset-0 grid place-items-center bg-red-600/70">
              <AlertCircle className="w-4 h-4 text-white" />
            </div>
          )}
          {onEdit && att.status !== 'error' && (
            <button
              type="button"
              onClick={onEdit}
              aria-label="Edit image"
              title="Edit (crop / rotate / flip)"
              className="absolute bottom-0.5 left-0.5 grid place-items-center w-5 h-5 rounded-full bg-stone-900/80 text-white hover:bg-stone-900 active:scale-95 transition"
            >
              <Pencil className="w-3 h-3" />
            </button>
          )}
        </>
      ) : (
        <>
          <div className="grid place-items-center w-9 h-9 rounded-lg bg-stone-100 dark:bg-stone-700 shrink-0">
            <Icon className="w-4 h-4 text-stone-500" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium truncate">{att.filename}</div>
            <div className={clsx(
              'text-[10px] flex items-center gap-1',
              att.status === 'error' ? 'text-red-500' : 'text-stone-500',
            )}>
              {(att.status === 'compressing' || att.status === 'uploading') && (
                <Loader2 className="w-3 h-3 animate-spin" />
              )}
              {att.status === 'error' && <AlertCircle className="w-3 h-3" />}
              <span>
                {att.status === 'compressing' ? 'Compressing…'
                  : att.status === 'uploading' ? 'Uploading…'
                  : att.status === 'error' ? (att.error || 'Failed')
                  : sizeLabel}
              </span>
            </div>
          </div>
        </>
      )}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${att.filename}`}
        title="Remove"
        className={clsx(
          'absolute top-0.5 right-0.5 grid place-items-center w-5 h-5 rounded-full',
          'bg-stone-900/70 text-white hover:bg-stone-900',
          isImage ? '' : 'static ml-1 bg-transparent text-stone-500 hover:bg-stone-100 dark:hover:bg-stone-700',
        )}
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  )
}


export function SharedComposer({ send, disabled = false }: { send: (text: string) => Promise<void>; disabled?: boolean }) {
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const input = useRef<HTMLTextAreaElement>(null)
  const scale = useUi((s) => s.textSizeScale)
  useEffect(() => {
    const editor = input.current
    if (!editor) return
    editor.style.height = 'auto'
    editor.style.height = `${Math.min(220, editor.scrollHeight)}px`
  }, [draft])
  async function submit() {
    if (busy || disabled || !draft.trim()) return
    setBusy(true)
    setError(null)
    try { await send(draft.trim()); setDraft('') }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not send. Retry this message.') }
    finally { setBusy(false) }
  }
  return <div className="border-t border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900 composer-pad relative">
    <div className="flex items-end gap-2 p-2">
      <div className="min-w-0 flex-1 flex items-end gap-1 rounded-3xl border bg-white dark:bg-stone-800 border-stone-200 dark:border-stone-700 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/30 px-3 py-1">
        <ComposerTextInput ref={input} scale={scale} aria-label="Message" value={draft} maxLength={8192}
          disabled={disabled || busy} enterKeyHint={window.matchMedia('(pointer: coarse)').matches ? 'enter' : 'send'}
          onChange={(e) => { setDraft(e.target.value); e.target.style.height = 'auto'; e.target.style.height = `${Math.min(220, e.target.scrollHeight)}px` }}
          onFocus={() => window.dispatchEvent(new CustomEvent('agentchat:composer-focus'))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.nativeEvent.isComposing && !e.shiftKey
              && (!window.matchMedia('(pointer: coarse)').matches || e.ctrlKey || e.metaKey)) {
              e.preventDefault(); void submit()
            }
          }} />
      </div>
      <button type="button" aria-label="Send" onClick={() => void submit()} disabled={disabled || busy || !draft.trim()}
        onPointerDown={(e) => e.preventDefault()}
        className="grid place-items-center w-11 h-11 rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:bg-stone-300 dark:disabled:bg-stone-700 disabled:text-stone-400 disabled:cursor-not-allowed transition-all duration-150 shadow-md shrink-0 active:scale-95">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 -ml-0.5" />}
      </button>
    </div>
    {error && <p role="alert" className="px-3 pb-2 text-sm text-red-600">{error}</p>}
  </div>
}
